import {
  reportPath,
  type AudienceGroup,
  type BlueprintPreview,
  type ColorBlock,
  type CashEntryKind,
  type EventAnalytics,
  type EventPhase,
  type EventComparison,
  type StoredRemindAudience,
  type SlotBookingMode,
  type SlotKind,
  type EventReportId,
  type LabelColorRules,
  type LabelGridId,
  type NextStepKey,
  type NextStepState,
  type OrgRole,
  type Permission,
  type PaymentMethod,
  type PosterLayout,
  type PayoutRoundingStep,
  type RequestReminderHours,
  type ShiftReminderMode,
  type PosterQrMode,
  type ReportDefinition,
  type SnapshotResponse,
  type SyncRequest,
  type SyncResponse,
  type SellerDataMode,
  type RegistrationKind,
} from '@barso/shared';
import {
  ApiClientError,
  apiDownload,
  apiRequest,
  TIMEOUT_LOGOUT_MS,
  TIMEOUT_LONG_MS,
} from './client';

/** Freiwillige Postanschrift (Straße mit Hausnummer, PLZ, Ort); Regeln in `@barso/shared` (`checkPostalAddress`). */
export interface PostalAddressValue {
  street: string;
  postalCode: string;
  city: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  /** Eigene freiwillige Postanschrift, `null` wenn nicht hinterlegt. */
  address: PostalAddressValue | null;
  emailVerified: boolean;
  platformRole: 'user' | 'platform_admin';
  /** Zwei-Faktor-Anmeldung (TOTP) ist eingerichtet. */
  totpEnabled: boolean;
}

/** Angemeldetes Gerät („Meine Geräte“, ACC-06). */
export interface DeviceSession {
  id: string;
  deviceLabel: string | null;
  startedAt: string;
  lastActiveAt: string;
  expiresAt: string;
  persistent: boolean;
  current: boolean;
}

export type OrgKind = 'club' | 'daycare' | 'school' | 'private' | 'other';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  kind: OrgKind;
  /** Rolle der angemeldeten Person. */
  role: OrgRole;
}

/** Kennzahlen des Organisations-Hubs (Mitglieder und Einladungen nur für Verwaltende). */
export interface OrganizationCounts {
  /** Laufende Basare (nicht abgeschlossen, abgerechnet, archiviert). */
  events: number;
  members?: number;
  openInvitations?: number;
}

export interface OrganizationDetail extends Organization {
  counts?: OrganizationCounts;
}

export interface Member {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: OrgRole;
  joinedAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  role: OrgRole;
  expiresAt: string;
  createdAt: string;
  /** Beim Annehmen vorgesehene Basar-Rollen (Einladungs-Matrix). */
  eventRoles: { eventId: string; eventTitle: string; role: TeamRole }[];
}

export type BatchInviteStatus =
  'invited' | 'assigned' | 'already_member' | 'rate_limited' | 'limit_reached';

export interface BatchInviteResult {
  email: string;
  status: BatchInviteStatus;
  message?: string;
}

export interface LoginResult {
  accessToken: string;
  expiresInSeconds: number;
  user: User;
}

const publicRoute = { auth: false } as const;

export const api = {
  /** Versions-Sperre der nativen App (P5): öffentlich, ohne Anmeldung abrufbar. */
  version: () =>
    apiRequest<{
      name: string;
      apiVersion: string;
      minMobileAndroidVersion: string | null;
      minMobileIosVersion: string | null;
      /** Fehlerberichte an GlitchTip (siehe errorReporting.ts), `null` = keine. */
      errorReporting: { dsn: string; environment: string; release: string | null } | null;
      /** Google Analytics (Mess-ID), `null` = aus. Geladen wird erst nach Einwilligung (analytics/analytics.ts). */
      analytics: { gaMeasurementId: string } | null;
    }>('GET', '/version', undefined, publicRoute),
  register: (input: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    acceptTerms: true;
    confirmAge16: true;
  }) => apiRequest<{ status: 'ok' }>('POST', '/auth/register', input, publicRoute),
  verifyEmail: (token: string, password: string) =>
    apiRequest<undefined>('POST', '/auth/verify-email', { token, password }, publicRoute),
  resendVerification: (email: string) =>
    apiRequest<{ status: 'ok' }>('POST', '/auth/resend-verification', { email }, publicRoute),
  login: (email: string, password: string, remember = true, code?: string) =>
    apiRequest<LoginResult>(
      'POST',
      '/auth/login',
      { email, password, clientType: 'web', remember, ...(code === undefined ? {} : { code }) },
      publicRoute,
    ),
  /** Mit der Kennung des Abos dieses Geräts für Mitteilungen (wird beim Abmelden mit gelöscht). */
  logout: (pushSubscriptionId?: string) =>
    apiRequest<undefined>(
      'POST',
      '/auth/logout',
      pushSubscriptionId === undefined ? undefined : { pushSubscriptionId },
      {
        ...publicRoute,
        timeoutMs: TIMEOUT_LOGOUT_MS,
        idempotent: true,
      },
    ),
  logoutAll: () => apiRequest<undefined>('POST', '/auth/logout-all'),
  totpStatus: () =>
    apiRequest<{ enabled: boolean; recoveryCodesLeft: number }>('GET', '/auth/totp'),
  totpSetup: (password: string) =>
    apiRequest<{ secret: string; otpauthUri: string; qrDataUrl: string }>(
      'POST',
      '/auth/totp/setup',
      { password },
    ),
  totpEnable: (code: string) =>
    apiRequest<{ recoveryCodes: string[] }>('POST', '/auth/totp/enable', { code }),
  totpDisable: (password: string, code: string) =>
    apiRequest<undefined>('POST', '/auth/totp/disable', { password, code }),
  totpRecoveryCodes: (password: string, code: string) =>
    apiRequest<{ recoveryCodes: string[] }>('POST', '/auth/totp/recovery-codes', {
      password,
      code,
    }),
  /** Alle eigenen Daten (ACC-07), Aufbau siehe API `GET /me/export`. */
  exportData: () => apiRequest<Record<string, unknown>>('GET', '/me/export'),
  deleteAccount: (password: string, code?: string) =>
    apiRequest<undefined>('POST', '/me/delete', {
      password,
      ...(code === undefined ? {} : { code }),
    }),
  sessions: () => apiRequest<DeviceSession[]>('GET', '/auth/sessions'),
  revokeSession: (sessionId: string) =>
    apiRequest<undefined>('DELETE', `/auth/sessions/${sessionId}`),
  forgotPassword: (email: string) =>
    apiRequest<{ status: 'ok' }>('POST', '/auth/forgot-password', { email }, publicRoute),
  resetPassword: (token: string, password: string) =>
    apiRequest<undefined>('POST', '/auth/reset-password', { token, password }, publicRoute),

  me: () => apiRequest<User>('GET', '/me'),
  updateMe: (changes: {
    firstName?: string;
    lastName?: string;
    phone?: string | null;
    /** `null` löscht die Anschrift. */
    address?: PostalAddressValue | null;
  }) => apiRequest<User>('PATCH', '/me', changes),
  /** Wunschnummer als Verkäufer (`null` = kein Wunsch); gilt bei der nächsten Vergabe. */
  sellerNumberWish: () =>
    apiRequest<{ sellerNumber: number | null }>('GET', '/me/seller-number-wish'),
  setSellerNumberWish: (sellerNumber: number | null) =>
    apiRequest<{ sellerNumber: number | null }>('PUT', '/me/seller-number-wish', { sellerNumber }),

  listOrganizations: () => apiRequest<Organization[]>('GET', '/orgs'),
  createOrganization: (input: { name: string; kind: OrgKind; confirmAdult: true }) =>
    apiRequest<Organization>('POST', '/orgs', input),
  getOrganization: (orgId: string) => apiRequest<OrganizationDetail>('GET', `/orgs/${orgId}`),
  updateOrganization: (orgId: string, changes: { name?: string; kind?: OrgKind }) =>
    apiRequest<Organization>('PATCH', `/orgs/${orgId}`, changes),
  getOrgTax: (orgId: string) => apiRequest<OrgTax>('GET', `/orgs/${orgId}/tax`),
  updateOrgTax: (orgId: string, changes: Partial<OrgTax>) =>
    apiRequest<OrgTax>('PATCH', `/orgs/${orgId}/tax`, changes),
  listMailTemplates: (orgId: string) =>
    apiRequest<{ templates: MailTemplateEntry[] }>('GET', `/orgs/${orgId}/mail-templates`),
  saveMailTemplate: (orgId: string, key: string, text: MailTemplateText) =>
    apiRequest<MailTemplateEntry>('PUT', `/orgs/${orgId}/mail-templates/${key}`, text),
  resetMailTemplate: (orgId: string, key: string) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/mail-templates/${key}`),
  previewMailTemplate: (orgId: string, key: string, text: MailTemplateText) =>
    apiRequest<{ subject: string; text: string }>(
      'POST',
      `/orgs/${orgId}/mail-templates/${key}/preview`,
      text,
    ),
  getOrgDefaults: (orgId: string) => apiRequest<OrgDefaults>('GET', `/orgs/${orgId}/defaults`),
  updateOrgDefaults: (orgId: string, defaults: OrgDefaults) =>
    apiRequest<OrgDefaults>('PUT', `/orgs/${orgId}/defaults`, defaults),
  // Verkäufer-Gruppen der Organisation (Welle 2b)
  listSellerGroups: (orgId: string) =>
    apiRequest<SellerGroup[]>('GET', `/orgs/${orgId}/seller-groups`),
  createSellerGroup: (orgId: string, input: SellerGroupInput & { isCollective: boolean }) =>
    apiRequest<SellerGroup>('POST', `/orgs/${orgId}/seller-groups`, input),
  updateSellerGroup: (orgId: string, groupId: string, changes: Partial<SellerGroupInput>) =>
    apiRequest<SellerGroup>('PATCH', `/orgs/${orgId}/seller-groups/${groupId}`, changes),
  deleteSellerGroup: (orgId: string, groupId: string) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/seller-groups/${groupId}`),
  listSellerGroupMembers: (orgId: string, groupId: string) =>
    apiRequest<SellerGroupMember[]>('GET', `/orgs/${orgId}/seller-groups/${groupId}/members`),
  addSellerGroupMembers: (orgId: string, groupId: string, emails: string[]) =>
    apiRequest<SellerGroupAddResult>('POST', `/orgs/${orgId}/seller-groups/${groupId}/members`, {
      emails,
    }),
  importSellerGroupMembers: (orgId: string, groupId: string, csv: string) =>
    apiRequest<SellerGroupAddResult>(
      'POST',
      `/orgs/${orgId}/seller-groups/${groupId}/members/import`,
      { csv },
    ),
  removeSellerGroupMember: (orgId: string, groupId: string, memberId: string) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/seller-groups/${groupId}/members/${memberId}`),
  collectSellerGroup: (orgId: string, groupId: string, eventId: string) =>
    apiRequest<{ added: number }>('POST', `/orgs/${orgId}/seller-groups/${groupId}/collect`, {
      eventId,
    }),
  getLetterhead: (orgId: string) => apiRequest<Letterhead>('GET', `/orgs/${orgId}/letterhead`),
  updateLetterhead: (orgId: string, changes: LetterheadChanges) =>
    apiRequest<Letterhead>('PATCH', `/orgs/${orgId}/letterhead`, changes),
  uploadLetterheadLogo: (orgId: string, dataBase64: string) =>
    apiRequest<Letterhead>(
      'PUT',
      `/orgs/${orgId}/letterhead/logo`,
      { dataBase64 },
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  removeLetterheadLogo: (orgId: string) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/letterhead/logo`),
  letterheadLogo: async (orgId: string): Promise<Blob> =>
    (await apiDownload('GET', `/orgs/${orgId}/letterhead/logo`, undefined, 'image/png, image/jpeg'))
      .blob,
  leaveOrganization: (orgId: string) => apiRequest<undefined>('POST', `/orgs/${orgId}/leave`),

  listMembers: (orgId: string) => apiRequest<Member[]>('GET', `/orgs/${orgId}/members`),
  changeMemberRole: (orgId: string, userId: string, role: OrgRole) =>
    apiRequest<undefined>('PATCH', `/orgs/${orgId}/members/${userId}`, { role }),
  removeMember: (orgId: string, userId: string) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/members/${userId}`),

  listInvitations: (orgId: string) => apiRequest<Invitation[]>('GET', `/orgs/${orgId}/invitations`),
  invite: (orgId: string, input: { email: string; role: 'admin' | 'member' }) =>
    apiRequest<Invitation>('POST', `/orgs/${orgId}/invitations`, input),
  /** Einladungs-Matrix: mehrere Adressen, Basar-Rollen gleich mit vormerken; Ergebnis je Adresse. */
  inviteMany: (
    orgId: string,
    input: {
      emails: string[];
      role: 'admin' | 'member';
      eventRoles: { eventId: string; role: TeamRole }[];
    },
  ) => apiRequest<BatchInviteResult[]>('POST', `/orgs/${orgId}/invitations/batch`, input),
  revokeInvitation: (orgId: string, invitationId: string) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/invitations/${invitationId}`),
  acceptInvitation: (token: string) =>
    apiRequest<Organization>('POST', '/invitations/accept', { token }),
  /** Vorschau ohne Anmeldung: nur Organisation und Rolle (ungültig: Fehler). */
  previewInvitation: (token: string) =>
    apiRequest<{ organizationName: string; role: string }>(
      'POST',
      '/public/invitations/preview',
      { token },
      publicRoute,
    ),
};

export interface PlatformOrganization {
  id: string;
  name: string;
  slug: string;
  kind: OrgKind;
  status: 'pending' | 'active' | 'suspended';
  ownerName: string | null;
  ownerEmail: string | null;
  memberCount: number;
  eventCount: number;
  createdAt: string;
  deleted: boolean;
}

export type UserStatus = 'pending' | 'active' | 'blocked';

/** Zeile der Nutzerverwaltung (Plan Nr. 21): bewusst ohne Telefonnummer (Datenschutz). */
export interface PlatformUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: UserStatus;
  platformRole: 'user' | 'platform_admin';
  createdAt: string;
}

/** Zeile des Audit-Logs (nur lesend, Plan Nr. 21). */
export interface AuditLogEntry {
  id: number;
  createdAt: string;
  actorId: string | null;
  actorEmail: string | null;
  organizationId: string | null;
  eventId: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  details: Record<string, unknown> | null;
}

/** Kennzahlen-Dashboard der Plattform (Plan Nr. 21). */
export interface PlatformDashboard {
  organizations: { total: number; active: number; suspended: number };
  events: { total: number; byStatus: Record<string, number> };
  users: { total: number; active: number };
  licenses: {
    activePaid: number;
    revenueGrossCentsTotal: number;
    revenueGrossCentsThisMonth: number;
  };
  /** Stand der Demo-Daten, `null`: keine Demo-Daten eingespielt. */
  demoData?: { version: number; appliedAt: string | null; image: string | null } | null;
}

export interface PlatformLicense {
  licenseId: string;
  eventTitle: string;
  organizationName: string;
  tier: string;
  pendingTier: string | null;
  status: string;
  priceGrossCents: number;
  vatPercent: number;
  createdAt: string;
  activatedAt: string | null;
}

export type PromoDiscountType = 'free' | 'percentage' | 'fixed_amount';

/** Promocode für kostenlose Lizenz-Freischaltung (LIC-13, ADM-05). */
export interface PromoCode {
  id: string;
  code: string;
  tier: LicenseTier | null;
  discountType: PromoDiscountType;
  discountValue: number | null;
  maxRedemptions: number | null;
  maxRedemptionsPerOrganization: number;
  redemptionsCount: number;
  expiresAt: string | null;
  status: 'active' | 'disabled';
  note: string | null;
  createdAt: string;
}

export interface PromoCodeInput {
  code: string;
  tier?: LicenseTier | null;
  discountType?: PromoDiscountType;
  discountValue?: number | null;
  maxRedemptions?: number | null;
  maxRedemptionsPerOrganization?: number;
  expiresAt?: string | null;
  note?: string | null;
}

/** Plattform-Einstellungen (Migration 0021): Mistral-API-Schlüssel und Mailing/SMTP. Geheimnisse
 * kommen nie im Klartext zurück, nur als „gesetzt". */
export interface PlatformSettings {
  /** `source`: woher der wirksame Schlüssel kommt (Einstellungen vor Umgebungsvariable `MISTRAL_API_KEY`). */
  mistralApiKey: { isSet: boolean; source?: 'settings' | 'env' | null };
  /** Hauptschalter der Foto-Erkennung (C7), Standard aus. */
  photoRecognitionEnabled: boolean;
  /** Hauptschalter der Dauer-Etiketten (Pool-Codes je Person), Standard aus. */
  poolLabelsEnabled: boolean;
  smtp: {
    host: string | null;
    port: number | null;
    secure: boolean | null;
    user: string | null;
    from: string | null;
    passwordIsSet: boolean;
  };
}

/** Feld weglassen lässt es unverändert, `null` löscht es (Rückfall auf die Umgebungsvariable). */
export interface PlatformSettingsUpdate {
  mistralApiKey?: string | null;
  photoRecognitionEnabled?: boolean | null;
  poolLabelsEnabled?: boolean | null;
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpSecure?: boolean | null;
  smtpUser?: string | null;
  smtpPassword?: string | null;
  smtpFrom?: string | null;
}

/** Ergebnis der Vergabe eines befristeten Support-Zugriffs (Plan Nr. 27). */
export interface SupportAccessGrant {
  eventId: string;
  grantedTo: string;
  expiresAt: string;
}

/** Sicht der Organisation auf den Support-Zugriff: nie, wer ihn vergeben hat (Datenschutz des Plattform-Teams). */
export interface SupportAccessStatus {
  active: boolean;
  expiresAt: string | null;
}

/** Vorhandene Datenbank-Sicherung (Plan Nr. 28), nur zur Anzeige und zum Download. */
export interface BackupFile {
  name: string;
  sizeBytes: number;
  createdAt: string;
}

/** Plattform-Verwaltung (nur Plattform-Admins). */
/** Ausgabe der Schicht-PDFs: mit Namen, anonym (nur Belegung) oder leer zum Ausfüllen. */
export type PdfVariant = 'names' | 'anonymous' | 'blank';

/** Query für die Schicht-PDFs; die Standardvariante „names“ wird nicht mitgeschickt. */
function pdfQuery(shiftId?: string, variant?: PdfVariant): string {
  const params = new URLSearchParams();
  if (shiftId !== undefined) params.set('shiftId', shiftId);
  if (variant !== undefined && variant !== 'names') params.set('variant', variant);
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

export const platformApi = {
  organizations: (status?: 'pending' | 'active' | 'suspended') =>
    apiRequest<PlatformOrganization[]>(
      'GET',
      `/platform/organizations${status === undefined ? '' : `?status=${status}`}`,
    ),
  setOrganizationStatus: (organizationId: string, status: 'active' | 'suspended') =>
    apiRequest<undefined>('PATCH', `/platform/organizations/${organizationId}/status`, { status }),
  licenses: () => apiRequest<PlatformLicense[]>('GET', '/platform/licenses'),
  activateLicense: (licenseId: string) =>
    apiRequest<unknown>('POST', `/platform/licenses/${licenseId}/activate`),
  promoCodes: () => apiRequest<PromoCode[]>('GET', '/platform/promo-codes'),
  createPromoCode: (input: PromoCodeInput) =>
    apiRequest<PromoCode>('POST', '/platform/promo-codes', input),
  setPromoCodeStatus: (id: string, status: 'active' | 'disabled') =>
    apiRequest<PromoCode>('PATCH', `/platform/promo-codes/${id}/status`, { status }),
  /** Fassungen eines Rechtstexts, neueste zuerst (max. 50). */
  legalTextHistory: (kind: string) =>
    apiRequest<LegalText[]>('GET', `/platform/legal-texts/${kind}/history`),
  /** Legt eine neue, jetzt aktuelle Fassung des Rechtstexts an (append-only). */
  setLegalText: (kind: string, content: string) =>
    apiRequest<LegalText>('PUT', `/platform/legal-texts/${kind}`, { content }),
  /** Nutzerverwaltung (Plan Nr. 21): durchsuchbar nach E-Mail, filterbar nach Status. */
  users: (filter: { query?: string; status?: UserStatus } = {}) => {
    const params = new URLSearchParams();
    if (filter.query !== undefined && filter.query !== '') params.set('query', filter.query);
    if (filter.status !== undefined) params.set('status', filter.status);
    const suffix = params.size === 0 ? '' : `?${params.toString()}`;
    return apiRequest<PlatformUser[]>('GET', `/platform/users${suffix}`);
  },
  setUserStatus: (userId: string, status: 'active' | 'blocked') =>
    apiRequest<undefined>('PATCH', `/platform/users/${userId}/status`, { status }),
  /** Audit-Log (nur lesend, Plan Nr. 21): Filter nach Aktion, Zeitraum und handelnder Person. */
  auditLog: (
    filter: {
      action?: string;
      from?: string;
      to?: string;
      actorId?: string;
      actorEmail?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (filter.action !== undefined && filter.action !== '') params.set('action', filter.action);
    if (filter.from !== undefined && filter.from !== '') params.set('from', filter.from);
    if (filter.to !== undefined && filter.to !== '') params.set('to', filter.to);
    if (filter.actorId !== undefined && filter.actorId !== '')
      params.set('actorId', filter.actorId);
    if (filter.actorEmail !== undefined && filter.actorEmail !== '') {
      params.set('actorEmail', filter.actorEmail);
    }
    const suffix = params.size === 0 ? '' : `?${params.toString()}`;
    return apiRequest<AuditLogEntry[]>('GET', `/platform/audit-log${suffix}`);
  },
  dashboard: () => apiRequest<PlatformDashboard>('GET', '/platform/dashboard'),
  /** Mistral-API-Schlüssel und Mailing/SMTP (Migration 0021). */
  settings: () => apiRequest<PlatformSettings>('GET', '/platform/settings'),
  updateSettings: (update: PlatformSettingsUpdate) =>
    apiRequest<PlatformSettings>('PUT', '/platform/settings', update),
  /** Befristeter Support-Zugriff der Plattform auf einen Basar (Plan Nr. 27), Standard 4 Stunden. */
  grantSupportAccess: (eventId: string, hours?: number) =>
    apiRequest<SupportAccessGrant>(
      'POST',
      `/platform/events/${eventId}/support-access`,
      hours === undefined ? {} : { hours },
    ),
  revokeSupportAccess: (eventId: string) =>
    apiRequest<undefined>('DELETE', `/platform/events/${eventId}/support-access`),
  /** Sichtbarkeit vorhandener Datenbank-Sicherungen (Plan Nr. 28), nur lesend + Download. */
  backups: () => apiRequest<BackupFile[]>('GET', '/platform/backups'),
  downloadBackup: (name: string) =>
    apiDownload('GET', `/platform/backups/${name}/download`, undefined, 'application/gzip').then(
      (r) => r.blob,
    ),
};

/**
 * Rechtstext, im Administrationsbereich pflegbar (append-only: jede Änderung legt eine neue, unveränderliche Fassung an).
 * Aktuell gibt es genau `kind: "license_booking_waiver"` (Widerrufsrechtsverzicht bei kostenpflichtiger Lizenz-Buchung).
 */
export interface LegalText {
  id: string;
  kind: string;
  content: string;
  createdAt: string;
}

export type TaxStatus = 'small_business' | 'regular' | 'non_profit' | 'private';

/** Steuerangaben der Organisation für Belege (Angaben des Veranstalters, keine steuerliche Aussage von BARSO). */
/** Verkäufer-Gruppe einer Organisation (Welle 2b). */
export interface SellerGroupInput {
  name: string;
  skipSellerLimit: boolean;
  showBonus: boolean;
  /** Bezeichnung der festen Bonusstufe; wird am Basar über die Bezeichnung der Stufe zugewiesen. */
  fixedBonusTierLabel: string | null;
}
export interface SellerGroup extends SellerGroupInput {
  id: string;
  isCollective: boolean;
  memberCount: number;
  createdAt: string;
}
export interface SellerGroupMember {
  id: string;
  email: string;
}
export interface SellerGroupAddResult {
  added: number;
  duplicates: number;
  invalid: { line: number; text: string }[];
}
export interface SellerGroupActivation {
  approved: number;
  alreadyApproved: number;
  skipped: number;
  limitReached: number;
  noAccount: number;
  tierFound: boolean | null;
}

/** Vorgaben einer Organisation für neue Basare (`null` = keine Vorgabe). */
export interface OrgDefaults {
  inactivityEnabled: boolean | null;
  inactivityRemindDays: number | null;
  inactivityGraceDays: number | null;
  labelColorRules: LabelColorRules | null;
  labelNameMode: 'number_first_name' | 'number_initial' | 'number_only' | null;
}

export interface OrgTax {
  taxStatus: TaxStatus | null;
  taxNumber: string | null;
  vatId: string | null;
  addressStreet: string | null;
  addressPostalCode: string | null;
  addressCity: string | null;
}

/** Platzhalter einer Mailvorlage der Organisation (Whitelist des Servers). */
export interface MailTemplatePlaceholder {
  name: string;
  label: string;
  example: string;
}

/** Eine Mailart mit Beispieltext und (falls vorhanden) dem eigenen Text der Organisation. */
export interface MailTemplateEntry {
  key: string;
  title: string;
  when: string;
  placeholders: MailTemplatePlaceholder[];
  defaultSubject: string;
  defaultBody: string;
  custom: { body: string; updatedAt: string } | null;
}

export interface MailTemplateText {
  body: string;
}

/** Briefkopf der Organisation für Druckdokumente (Angaben des Veranstalters); das Logo liefert eine eigene Route. */
export interface Letterhead {
  name: string;
  addressStreet: string | null;
  addressPostalCode: string | null;
  addressCity: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  contactWebsite: string | null;
  footerText: string | null;
  taxLine: string | null;
  hasLogo: boolean;
}

/** Editierbare Felder des Briefkopfs (Anschrift und Steuerangaben pflegt `OrgTax`). */
export interface LetterheadChanges {
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactWebsite?: string | null;
  footerText?: string | null;
}

export type EventStatus =
  | 'draft'
  | 'published'
  | 'registration_open'
  | 'intake'
  | 'selling'
  | 'returns'
  | 'closed'
  | 'settled'
  | 'archived';

export type LicenseTier = 'free' | 'standard' | 'large' | 'unlimited';

export interface EventLicense {
  tier: LicenseTier;
  itemLimit: number | null;
  itemsCreated: number;
  priceGrossCents: number;
  vatPercent: number;
  status: 'pending' | 'active' | 'cancelled' | 'refunded';
  /** Gewünschte größere Stufe, wirksam erst nach Freischaltung durch die Plattform. */
  pendingTier: LicenseTier | null;
}

export type SettlementModel = 'mediation' | 'commission';

export interface BazaarEvent {
  id: string;
  organizationId: string;
  slug: string;
  title: string;
  description: string | null;
  status: EventStatus;
  venue: string | null;
  startsAt: string | null;
  endsAt: string | null;
  closesAt: string | null;
  registrationOpensAt: string | null;
  registrationClosesAt: string | null;
  /** Vorrangfenster (X1): ab hier dürfen eingeladene frühere Verkäufer sich anmelden, `null` = keines. */
  priorityRegistrationOpensAt?: string | null;
  commissionBasisPoints: number;
  sellerFeeCents: number;
  maxSellers: number | null;
  maxItemsPerSeller: number;
  expectedItems: number;
  labelNameMode: 'number_first_name' | 'number_initial' | 'number_only';
  settlementModel: SettlementModel;
  marketplaceListed: boolean;
  selfRegistrationMode: SelfRegistrationMode;
  /** Automatische Anmeldung bei voller Höchstzahl: `true` = Warteliste, sonst Ablehnung (Standard). Fehlt bei älteren Antworten. */
  waitlistWhenFull?: boolean;
  shiftBypassesSellerLimit?: boolean;
  inactivityEnabled?: boolean;
  inactivityRemindDays?: number;
  inactivityGraceDays?: number;
  /** Erste Verkäufernummer (Standard 1). */
  sellerNumberStart?: number;
  /** Es ist schon eine Verkäufernummer vergeben: Die Startnummer ist dann gesperrt. */
  sellerNumbersAssigned?: boolean;
  /** Vorgegebene Kategorien und Größen für Artikel (`null` = freie Eingabe). */
  itemCategories: string[] | null;
  itemSizes: string[] | null;
  /** Foto-Erkennung (C7) für Verkäufer dieses Basars erlaubt (wirkt nur mit dem Hauptschalter der Plattform). */
  photoRecognitionEnabled: boolean;
  /** Rückgabe-Station bietet den Korb-Modus an (O4, Standard aus). Der Server liefert es immer mit. */
  returnsBasketMode?: boolean;
  /** Farbblöcke auf Etiketten (O5): bis zu 12 geordnete Regeln, `null` = aus. Der Server liefert es immer mit. */
  labelColorRules?: LabelColorRules | null;
  /** Zeitfenster für Abgabe und Abholung (X8): aus, offen (Verkäufer buchen selbst), geschlossen (nur der Veranstalter teilt zu). */
  slotBooking?: SlotBookingMode;
  /** Auszahlung abrunden (X6): 0 = aus, sonst 10, 50 oder 100 Cent. */
  payoutRoundingCents: PayoutRoundingStep;
  /** Mindestprovision je Verkäufer in Cent (0 = keine), Helfer ohne Provision (Standard ja) und ohne Gebühr (Standard nein). */
  minCommissionCents?: number;
  helperCommissionExempt?: boolean;
  helperFeeExempt?: boolean;
  /** Verkäufergebühr nur, wenn der Verkäufer etwas verkauft hat (Standard nein = immer). */
  feeOnlyIfSold?: boolean;
  /** Preisregeln (Paket B): Mindestpreis je Artikel in Cent (Standard 50) und Preisschritt in Cent (Standard 1). */
  minItemPriceCents?: number;
  /** Bestehende Artikel, die den Preisregeln nicht entsprechen (nur Anzeige, nur Einzelansicht). */
  priceRuleMismatchCount?: number;
  priceStepCents?: number;
  /** Rabattaktionen sind für diesen Basar erlaubt (X7, Standard aus). Der Server liefert es immer mit. */
  discountPromosEnabled?: boolean;
  /** Zahlart „Karte“ als Markierung an der Kasse (U14, Standard aus): keine Anbindung, keine Zahlungsabwicklung. */
  cardPaymentEnabled?: boolean;
  /** Kaufbeleg (Quittung als PDF) beim Abkassieren (Welle 4f, Standard aus): steuerliche Behandlung (TSE) ist Sache des Veranstalters. */
  invoiceEnabled?: boolean;
  /** KI-Artikelliste hochladen (Welle 4h, Standard aus): Text oder Foto geht an Mistral AI, die Verkäufer prüfen Entwürfe. */
  aiListImportEnabled?: boolean;
  /** KI-Bildzuordnung an der Annahme (Welle 4k, Standard aus): das Foto eines Artikels ohne Etikett geht an Mistral AI, das Team bestätigt den Vorschlag. */
  aiImageMatchEnabled?: boolean;
  /** Artikelbilder (Welle 4g, Standard aus): Verkäufer dürfen ein Foto je Artikel hochladen, der Server verkleinert es. */
  itemImagesEnabled?: boolean;
  /** Verkäufer dürfen Artikel als Spende markieren (Standard an). */
  donationsEnabled?: boolean;
  /** Verkäufer dürfen vorhandene Etiketten (eigene Codes) zuordnen (Standard an). */
  externalCodesEnabled?: boolean;
  /** Zusatzfunktionen (Plan Welle 3, F0, Standard aus). Der Server liefert sie immer mit. */
  runSheetEnabled?: boolean;
  pushEnabled?: boolean;
  payoutTransferEnabled?: boolean;
  helperBonusEnabled?: boolean;
  /** Nachrichten und Erinnerungen je Basar (Standard: Vortag, 24 Stunden, beide Mitteilungsarten an). Der Server liefert sie immer mit. */
  shiftReminderMode?: ShiftReminderMode;
  requestReminderHours?: RequestReminderHours;
  pushSalesEnabled?: boolean;
  pushPickupEnabled?: boolean;
  /** Kasse (Paket F, Standard je an): Preiskorrektur und Verkauf ohne Etikett. Der Server liefert es immer mit. */
  tillPriceChangeEnabled?: boolean;
  tillNoLabelEnabled?: boolean;
  /** Vorlage des Verwendungszwecks der Auszahlung (Paket G), Platzhalter {basar} und {nummer}. */
  payoutRemittanceTemplate?: string;
  /** Standard-Etikettenformat (Schlüssel aus LABEL_GRIDS), Vorauswahl im Druckdialog. */
  labelGridDefault?: LabelGridId;
  /** Verkäufer sehen den Verkaufsstand vor der Abrechnung (Standard ja; Audit 21). */
  sellersSeeSales?: boolean;
  /** Online-Basar (Hinweis auf der öffentlichen Seite). */
  isOnline?: boolean;
  /** Verkäuferdaten bei der Anmeldung (Standard: Telefon aus, Anschrift freiwillig). */
  sellerPhoneMode?: SellerDataMode;
  sellerAddressMode?: SellerDataMode;
  /** Verkäufer bei Übernahme einer Helferschicht automatisch freischalten (Standard aus). */
  shiftAutoApprove?: boolean;
  /** Annahme vermerkt beim Check-in die Zustimmung zu den Bedingungen (Standard aus). */
  intakeConfirmTerms?: boolean;
  /** Zusatzfunktionen, die auf diesem System nicht einschaltbar sind, mit dem Grund (nur für Verwaltende). */
  unavailableFeatures?: Record<string, string>;
  /** Herkunft (nur Anzeige): `system:<key>@<version>`, `template:<id>`, `event:<id>`; `null` = leer angelegt. */
  createdFrom?: string | null;
  /** Nur bei GET für Verwaltende: offene Anfragen, freigegebene Verkäufer, Team. */
  counts?: {
    sellers: number;
    sellersApproved: number;
    helpers: number;
    team: number;
    itemsNotReceived: number;
  };
  /** Rechte der aufrufenden Person in diesem Basar (vom Server aus den Rollen aufgelöst). */
  myPermissions?: Permission[];
  /** Nur Inhaber/Verwalter der Organisation: Vorschläge früherer Verkäufer beim manuellen Hinzufügen. */
  canSearchContacts?: boolean;
  /** Recht `org.events.create` (Inhaber, Verwalter): „Als Vorlage speichern“. */
  canCreateEventTemplates?: boolean;
  license: EventLicense;
  allowedTransitions: EventStatus[];
}

/** Sicht für Helfer, Kasse und Annahme: ohne Provision, Gebühren, Lizenz und Statuswechsel. */
export type EventSummary = Pick<
  BazaarEvent,
  | 'id'
  | 'organizationId'
  | 'slug'
  | 'title'
  | 'description'
  | 'status'
  | 'venue'
  | 'startsAt'
  | 'endsAt'
  | 'closesAt'
  | 'registrationOpensAt'
  | 'registrationClosesAt'
  | 'labelNameMode'
  | 'returnsBasketMode'
  | 'runSheetEnabled'
  | 'pushEnabled'
  | 'payoutTransferEnabled'
  | 'helperBonusEnabled'
> & {
  /** Rechte der aufrufenden Person in diesem Basar (vom Server aus den Rollen aufgelöst). */
  myPermissions?: Permission[];
  canSearchContacts?: boolean;
  canCreateEventTemplates?: boolean;
};

/** Basar-Rollen (ORG-06): wer an diesem Basar Kasse/Annahme/Verwaltung machen darf. */
export type TeamRole = 'manager' | 'intake' | 'cashier' | 'helper';

export interface TeamMember {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: TeamRole;
}

/** Person, die diesem Basar zugeteilt werden könnte (schon Mitglied der Organisation). */
export interface TeamCandidate {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

/** Der Server entscheidet: Verwaltende bekommen den ganzen Basar, alle anderen die Kurzsicht. */
export function isFullEvent(event: BazaarEvent | EventSummary): event is BazaarEvent {
  return 'license' in event;
}

export interface EventInput {
  title?: string;
  description?: string | null;
  venue?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  /** Basar endet endgültig: bis dahin gelten Links und Zugänge für Schichten und Stationen. */
  closesAt?: string | null;
  registrationOpensAt?: string | null;
  registrationClosesAt?: string | null;
  priorityRegistrationOpensAt?: string | null;
  commissionBasisPoints?: number;
  sellerFeeCents?: number;
  maxSellers?: number | null;
  maxItemsPerSeller?: number;
  expectedItems?: number;
  labelNameMode?: 'number_first_name' | 'number_initial' | 'number_only';
  settlementModel?: SettlementModel;
  marketplaceListed?: boolean;
  selfRegistrationMode?: SelfRegistrationMode;
  waitlistWhenFull?: boolean;
  shiftBypassesSellerLimit?: boolean;
  inactivityEnabled?: boolean;
  inactivityRemindDays?: number;
  inactivityGraceDays?: number;
  sellerNumberStart?: number;
  itemCategories?: string[] | null;
  itemSizes?: string[] | null;
  photoRecognitionEnabled?: boolean;
  returnsBasketMode?: boolean;
  /** `null` oder eine leere Liste schaltet die Farbblöcke aus; ab Beginn der Annahme gesperrt (409 `EVENT_LOCKED`). */
  labelColorRules?: LabelColorRules | null;
  slotBooking?: SlotBookingMode;
  payoutRoundingCents?: PayoutRoundingStep;
  minCommissionCents?: number;
  helperCommissionExempt?: boolean;
  helperFeeExempt?: boolean;
  /** Verkäufergebühr nur, wenn der Verkäufer etwas verkauft hat (Standard nein = immer). */
  feeOnlyIfSold?: boolean;
  minItemPriceCents?: number;
  priceStepCents?: number;
  /** Rabattaktionen erlauben (X7): Verkäufer geben Artikel frei, der Veranstalter legt Aktionen an. */
  discountPromosEnabled?: boolean;
  cardPaymentEnabled?: boolean;
  invoiceEnabled?: boolean;
  aiListImportEnabled?: boolean;
  aiImageMatchEnabled?: boolean;
  itemImagesEnabled?: boolean;
  donationsEnabled?: boolean;
  externalCodesEnabled?: boolean;
  /** Zusatzfunktionen schalten; Einschalten nur für freigegebene Funktionen (sonst 400). */
  runSheetEnabled?: boolean;
  pushEnabled?: boolean;
  payoutTransferEnabled?: boolean;
  helperBonusEnabled?: boolean;
  shiftReminderMode?: ShiftReminderMode;
  requestReminderHours?: RequestReminderHours;
  pushSalesEnabled?: boolean;
  pushPickupEnabled?: boolean;
  tillPriceChangeEnabled?: boolean;
  tillNoLabelEnabled?: boolean;
  /** Vorlage des Verwendungszwecks der Auszahlung (Paket G), Platzhalter {basar} und {nummer}. */
  payoutRemittanceTemplate?: string;
  labelGridDefault?: LabelGridId;
  sellersSeeSales?: boolean;
  isOnline?: boolean;
  sellerPhoneMode?: SellerDataMode;
  sellerAddressMode?: SellerDataMode;
  shiftAutoApprove?: boolean;
  intakeConfirmTerms?: boolean;
  /** Nur beim Anlegen: aktiviert die Lizenz sofort (LIC-13). */
  promoCode?: string;
  /** Pflicht, sobald die gewählte/berechnete Stufe kostenpflichtig ist: die aktuell gültige Fassung des Widerrufshinweises. */
  waiverTextId?: string;
}

export type SelfRegistrationMode = 'approval' | 'auto';

/** Basar wiederholen (X1, Teil A): was mitgenommen wird. */
export interface CopyEventInput {
  title: string;
  /** Neuer Starttermin (ISO). Das Datum bestimmt die Verschiebung, die Uhrzeit bleibt wie im Vorbild. */
  startsAt: string;
  copy?: { schedule?: boolean; shifts?: boolean; terms?: boolean; team?: boolean };
}

export interface CopiedEvent extends BazaarEvent {
  copied: {
    schedule: number;
    shifts: number;
    team: number;
    terms: boolean;
    /** Neu mit der Blaupause (U16a); fehlt bei einem älteren Server. */
    timeSlots?: number;
    stations?: number;
  };
}

/** Standardsets und eigene Basar-Vorlagen (U16b): Karten mit Vorschau für den Assistenten „Basar anlegen“. */
export interface BlueprintSystemCard {
  key: string;
  version: number;
  name: string;
  /** Ein bis zwei Sätze. */
  description: string;
  /** Provision und Gebühr sind im Set leer: Der Assistent verlangt sie ausdrücklich. */
  needsSettlement: true;
  preview: BlueprintPreview;
}

export interface EventTemplate {
  id: string;
  name: string;
  description: string | null;
  /** Titel des Basars, aus dem die Vorlage entstand („erstellt aus ‚Herbstbasar 2026‘“). */
  sourceEventTitle: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlueprintTemplateCard extends EventTemplate {
  /** `false`: aus einer neueren Version oder beschädigt, dann ohne Vorschau (Anlegen ergibt 409). */
  usable: boolean;
  preview: BlueprintPreview | null;
}

export interface BlueprintCatalog {
  system: BlueprintSystemCard[];
  templates: BlueprintTemplateCard[];
}

/** Woraus ein neuer Basar entsteht (`POST /orgs/:orgId/events` mit `from`). */
export type CreateEventSource =
  | { kind: 'system'; key: string }
  | { kind: 'template'; id: string }
  | { kind: 'event'; id: string; team?: boolean };

/**
 * Basar anlegen mit Quelle: Alle Einstellungen kommen aus der Quelle, Zeiten relativ zum Basartag. `startsAt` ist der
 * Basartag (nur das Datum zählt), `saleFrom`/`saleTo` Beginn und Ende des Verkaufs. Bei Standardsets sind
 * `commissionBasisPoints` und `sellerFeeCents` Pflicht (ganze Cent, Provision in Basispunkten).
 */
export interface CreateEventFromInput {
  title: string;
  from: CreateEventSource;
  startsAt: string;
  saleFrom?: string;
  saleTo?: string;
  commissionBasisPoints?: number;
  sellerFeeCents?: number;
  expectedItems?: number;
  promoCode?: string;
  waiverTextId?: string;
}

/** Frühere Verkäufer einladen (X1, Teil B): nur Zahlen, keine Namen. */
export interface InvitePreview {
  eligible: number;
  alreadyInvited: number;
  optedOut: number;
}

/** Plan für den zeitgesteuerten Versand der Vorab-Mail (U16d). */
export interface InviteSchedule {
  runAt: string;
  sinceMonths: number;
  sourceEventIds: string[];
  status: 'planned' | 'done' | 'skipped';
  /** Fehlercode, wenn der Lauf den Versand ausgelassen hat. */
  skipReason: string | null;
  invitedCount: number | null;
  createdAt: string;
  doneAt: string | null;
}

/** Eigenes Anmeldefenster der angemeldeten Person (U16d). */
export interface RegistrationWindow {
  opensAt: string | null;
  early: boolean;
}

export interface InviteOptOut {
  organizationId: string;
  organizationName: string;
  optOut: boolean;
}

/** Lager (X2): unverkaufte Artikel früherer Basare derselben Organisation. */
export interface CarryoverGroup {
  eventId: string;
  eventTitle: string;
  endedAt: string | null;
  items: {
    itemId: string;
    itemNo: number;
    title: string;
    size: string | null;
    category: string | null;
    priceCents: number;
    donate: boolean;
    /** Der Preis passt nicht zu den Preisregeln dieses Basars: der Artikel würde übersprungen. */
    priceRuleError?: string | null;
  }[];
}

export interface CarryoverResult {
  created: Item[];
  skipped: { itemId: string; reason: 'already_carried' | 'price_rule' }[];
  /** `externalCode`: das vorhandene Etikett gilt im neuen Basar nicht weiter, für diesen Artikel ist ein neues Etikett nötig. */
  adjusted: { itemId: string; fields: ('size' | 'category' | 'externalCode')[] }[];
}

export interface PublicEvent {
  /** Basar-ID, für „Beim Basar helfen“ (Eintragen in Helferschichten). */
  eventId: string;
  /** Es gibt Schichten, in die sich Personen selbst eintragen können. */
  hasOpenHelperShifts: boolean;
  /** Selbst-Eintragung freigegeben (sonst Hinweis „ab …“ bzw. „noch nicht freigegeben“). */
  helperSignupOpen: boolean;
  helperSignupOpensAt: string | null;
  title: string;
  description: string | null;
  status: EventStatus;
  venue: string | null;
  startsAt: string | null;
  endsAt: string | null;
  registrationOpensAt: string | null;
  registrationClosesAt: string | null;
  sellerFeeCents: number;
  feeOnlyIfSold?: boolean;
  commissionBasisPoints: number;
  /** Online-Basar: findet online statt (Hinweis statt Ortsangabe). */
  isOnline?: boolean;
  /** Welche Profilangaben die Anmeldung abfragt (fehlt bei älterem Server: Telefon aus, Anschrift freiwillig). */
  sellerPhoneMode?: SellerDataMode;
  sellerAddressMode?: SellerDataMode;
  organizationName: string;
  registrationOpen: boolean;
  /** Basar-Bedingungen (Nr. 32), `null` = keine hinterlegt. */
  terms: { id: string; content: string } | null;
  /** Ablaufplan/Kalender, geordnet, leer = nichts hinterlegt. */
  schedule: ScheduleItem[];
  /** Plakat zum Herunterladen (erzeugt oder eigenes), `null` = nicht verfügbar. */
  poster: { kind: 'generated' | 'custom'; filename: string } | null;
  /** Logo des Basars (oder der Organisation) vorhanden. */
  hasLogo?: boolean;
  /** Sichtbare Bonusstufen (nie versteckte, nie „nur zuweisen“), leer ohne Helferbonus. */
  bonusTiers: PublicBonusTier[];
  /** Höchstzahl Verkäufer (`null` = keine Grenze). */
  maxSellers: number | null;
  /** Höchstzahl Artikel je Verkäufer ohne Bonus; `null` = nicht ausgewiesen (dann keine Artikelzahl anzeigen). */
  maxItemsPerSeller: number | null;
}

/** Eine Bonusstufe auf der Anmeldeseite. */
export interface PublicBonusTier {
  label: string;
  minShifts: number;
  extraItems: number | null;
  commissionBasisPoints: number | null;
  earlyEntryMinutes: number | null;
  feeCents: number | null;
}

/** Ein Termin im Ablaufplan/Kalender eines Basars (z. B. "Warenannahme", "Verkauf"). */
export interface ScheduleItem {
  id: string;
  label: string;
  startsAt: string;
  endsAt: string | null;
  note: string | null;
  /** Automatische Erinnerung (X3): Zielgruppe und Tage vorher, beides oder keins. Nur in der Verwaltung, nie öffentlich. */
  remindAudience?: StoredRemindAudience | null;
  remindDaysBefore?: number | null;
}

/** Zum Speichern: ohne `id` (die vergibt der Server neu, die ganze Liste wird ersetzt). */
export interface ScheduleItemInput {
  label: string;
  startsAt: string;
  endsAt: string | null;
  note: string | null;
  remindAudience?: StoredRemindAudience | null;
  remindDaysBefore?: number | null;
}

/** Eintrag im plattformweiten Basar-Kalender (EVT-11). */
export interface MarketplaceEntry {
  eventId: string;
  orgSlug: string;
  eventSlug: string;
  title: string;
  organizationName: string;
  venue: string | null;
  startsAt: string | null;
  endsAt: string | null;
  sellerSlotsRemaining: number | null;
  selfRegistrationMode: SelfRegistrationMode;
}

export type SellerStatus = 'applied' | 'approved' | 'waitlist' | 'rejected' | 'cancelled';

export interface Registration {
  id: string;
  eventId: string;
  sellerNumber: number | null;
  isHelper: boolean;
  labelName: string | null;
  status: SellerStatus;
  itemsCreated: number;
  createdAt: string;
  /** Auszahlungsart (W3-Auszahlung): `transfer` nur bei freigegebener Überweisung. Fehlt bei älteren Antworten (= bar). */
  payoutMethod?: PayoutMethod;
}

/** Vorschlag der Foto-Erkennung; jedes Feld kann `null` sein (nicht erkannt). */
export interface PhotoSuggestion {
  title: string | null;
  priceCents: number | null;
  category: string | null;
  size: string | null;
}

/** Ein Entwurf aus der KI-Artikelliste (Welle 4h): nur ein Vorschlag, angelegt wird erst nach Prüfung durch den Verkäufer. */
export interface ListDraft {
  title: string;
  /** Ganze Cent oder `null`, wenn kein gültiger Preis erkannt wurde (dann trägt der Verkäufer ihn von Hand ein). */
  priceCents: number | null;
  /** Grund, warum der erkannte Preis verworfen wurde (verständlicher Text), sonst `null`. */
  priceNote: string | null;
  category: string | null;
  size: string | null;
}

export interface ListDraftResult {
  drafts: ListDraft[];
  /** Es gab mehr Zeilen als Entwürfe erlaubt sind. */
  truncated: boolean;
}

/** Ein Vorschlag der KI-Bildzuordnung (Welle 4k): ein vorhandener, noch nicht angenommener Artikel des gewählten Verkäufers. */
export interface MatchCandidate {
  itemId: string;
  itemNo: number;
  title: string;
  size: string | null;
  category: string | null;
  /** Preis des vorhandenen Artikels in ganzen Cent. */
  priceCents: number;
  /** Grobe Einschätzung der KI, nur ein Hinweis. */
  confidence: 'high' | 'medium' | 'low';
}

export interface MatchResult {
  candidates: MatchCandidate[];
  /** Es gab mehr offene Artikel als verglichen werden; nur die ersten wurden berücksichtigt. */
  partial: boolean;
}

/** Der eigene Helferbonus (W3-Bonus-a). Fehlt oder `null`, wenn der Basar keinen führt. */
export interface MyBonus {
  labels: string[];
  extraItems: number;
  /** Provision nach Bonus und die des Basars (Basispunkte). */
  commissionBasisPoints: number;
  baseCommissionBasisPoints: number;
  earlyEntryMinutes: number | null;
  /** Zugesagte Schichten. */
  shiftsDone: number;
  /** Helfer-Verkäufer ohne Provision laut Einstellung des Basars (fehlt: wie `isHelper`). */
  commissionExempt?: boolean;
  nextTier: {
    label: string;
    minShifts: number;
    shiftsMissing: number;
    extraItems: number | null;
    commissionBasisPoints: number | null;
    earlyEntryMinutes: number | null;
    /** Andere Verkäufergebühr der nächsten Stufe in Cent, `null` = keine Änderung der Gebühr. */
    feeCents: number | null;
  } | null;
  /** Zählweise der Provision: zugesagte oder anwesend bestätigte Schichten. */
  commissionBasis: 'committed' | 'confirmed';
}

export interface MyRegistration extends Registration {
  /** Ware abgeholt: Zeitpunkt der Bestätigung bei der Übergabe, `null`/fehlt = nicht bestätigt. */
  pickupConfirmedAt?: string | null;
  bonus?: MyBonus | null;
  eventTitle: string;
  eventStatus: EventStatus;
  organizationName: string;
  orgSlug: string;
  eventSlug: string;
  maxItems: number;
  /** Vorgaben des Veranstalters (`null` = freie Eingabe). */
  itemCategories: string[] | null;
  itemSizes: string[] | null;
  /** Foto-Erkennung (C7) nutzbar: Plattform-Schalter, Basar-Schalter und Schlüssel (vom Server geprüft). */
  photoRecognition: boolean;
  /** KI-Artikelliste hochladen (Welle 4h) nutzbar: Plattform-Schalter, Basar-Schalter und Schlüssel (vom Server geprüft); fehlt = nein. */
  aiListImport?: boolean;
  /** Farbblöcke des Basars (O5) für die Vorschau der eigenen Etiketten, `null` = aus. */
  labelColorRules?: LabelColorRules | null;
  /** Standard-Etikettenformat des Basars (Vorauswahl im Druckdialog); fehlt bei älteren Antworten. */
  labelGridDefault?: LabelGridId;
  /** Verkaufsstand ist für den Verkäufer noch verborgen (Einstellung des Basars bis zur Abrechnung); fehlt = sichtbar. */
  salesHidden?: boolean;
  /** Der Basar erlaubt Rabattaktionen (X7): nur dann kann der Verkäufer Artikel dafür freigeben. */
  discountPromosEnabled?: boolean;
  /** Spende und vorhandene Etiketten erlaubt (fehlt = erlaubt, ältere Antworten). */
  donationsEnabled?: boolean;
  externalCodesEnabled?: boolean;
  /** Der Basar erlaubt Artikelbilder (Welle 4g, Standard aus); fehlt = nicht erlaubt. */
  itemImagesEnabled?: boolean;
  /** Preisregeln des Basars (Paket B) für die Eingabehilfe; fehlt = Standard (0,50 €, jeder Cent-Betrag). */
  minItemPriceCents?: number;
  priceStepCents?: number;
  /** Der Basar bietet die Auszahlung per Überweisung an (W3-Auszahlung): nur dann zeigt die Anmeldung die Wahl. */
  payoutTransferEnabled?: boolean;
}

/** Bonusstufe eines Basars (W3-Bonus-a). `manualOnly`: wird nur zugewiesen (z. B. „Orga-Team“). */
export interface BonusTier {
  id: string;
  position: number;
  label: string;
  minShifts: number;
  manualOnly: boolean;
  extraItems: number | null;
  commissionBasisPoints: number | null;
  earlyEntryMinutes: number | null;
  /** Andere Verkäufergebühr in Cent, `null` = Gebühr des Basars. */
  feeCents: number | null;
  /** „Infos verstecken“: wirkt, wird aber nie genannt (nicht für Verkäufer, nicht öffentlich). */
  hidden: boolean;
}

export type CommissionShiftBasis = 'committed' | 'confirmed';

export interface BonusTiersView {
  /** Was bei der Provision als Schicht zählt. */
  commissionBasis: CommissionShiftBasis;
  /** Früher Einlass gilt erst nach Freischaltung (Anwesenheit oder Orga), Standard ja. */
  entryRequiresUnlock: boolean;
  tiers: BonusTier[];
  locked: boolean;
  lockReason: string | null;
  baseCommissionBasisPoints: number;
  /** Verkäufergebühr des Basars in Cent: keine Stufengebühr darf darüber liegen. */
  baseFeeCents: number;
  /** Bonusstufen stehen auf der öffentlichen Anmeldeseite (Schalter, Standard aus). */
  publicShown: boolean;
}

export type BonusTierInput = Omit<BonusTier, 'id' | 'position'> & { id?: string };

/** Eine nicht vergebene Nummer der Lückenliste (Welle 1, Punkt 5). */
export interface SellerGap {
  sellerNumber: number;
  /** In wie vielen anderen Basaren der Organisation diese Nummer vergeben war. */
  earlierEvents: number;
  reserved: boolean;
  note: string | null;
}

export interface SellerGaps {
  numbers: SellerGap[];
  truncated: boolean;
}

/** Eine Zeile der Auswertung „lange nicht angeboten“. Zeit als ISO-Text. */
export interface NotOfferedEntry {
  firstName: string;
  lastName: string;
  email: string;
  times: number;
  lastRegistered: string;
}

/** VIP-Reservierung einer Verkäufernummer (Welle 1, Punkt 4). Zeiten als ISO-Text (UTC). */
export interface ReservedNumber {
  id: string;
  sellerNumber: number;
  /** Anmeldung dieses Basars, für die reserviert ist; ohne Namen. */
  registrationId: string | null;
  /** Ende der Reservierung, `null` = bis zur Löschung. */
  reservedUntil: string | null;
  note: string | null;
  createdAt: string;
  /** `false`, wenn die Ablaufzeit vorbei ist. */
  active: boolean;
}

export interface SellerEntry extends Registration {
  /** Kennzeichen des Helferbonus: Bezeichnungen der erreichten Stufen, zugewiesene Stufe. */
  bonus?: {
    labels: string[];
    assignedTierId: string | null;
    assignedLabel: string | null;
    orgaTeam: boolean;
    /** Mehr Artikel angelegt als die effektive Höchstzahl (zum Beispiel nach einer abgesagten Schicht). */
    overLimit: boolean;
  };
  firstName: string;
  lastName: string;
  email: string;
  /** Vom Inaktivitätslauf ausgenommen (Welle 1, Punkt 6). */
  inactivityExempt: boolean;
  /** Anmeldeart (Welle 2b); fehlt bei älterem Server (= normal). */
  registrationKind?: RegistrationKind;
}

export interface Item {
  id: string;
  itemNo: number;
  labelCode: string;
  labelCodeDisplay: string;
  title: string;
  size: string | null;
  category: string | null;
  priceCents: number;
  donate: boolean;
  status: 'listed' | 'received' | 'sold' | 'returned' | 'missing';
  printed: boolean;
  /** Übernommenes fremdes Etikett (z. B. EAN), sonst `null`. */
  externalCode: string | null;
  /** Der Verkäufer erlaubt die Reduzierung in einer Rabattaktion (X7). */
  discountAllowed?: boolean;
  /** Zum Artikel liegt ein Bild vor (Welle 4g); das Bild selbst lädt `bazaarApi.ownItemImage`. */
  hasImage?: boolean;
  /** Fassung des Bildes (Millisekunden), nur mit `hasImage`. */
  imageVersion?: number;
  /**
   * Besonderheiten des Verkaufs (U17i), nur bei verkauften Artikeln: an der Kasse ohne Etikett erfasst und von der Leitung
   * zugeordnet (mit Foto, wenn freigegeben), und/oder der Preis wurde an der Kasse korrigiert (Grund als Schlüssel).
   */
  saleInfo?: {
    manualAssigned: boolean;
    photoId: string | null;
    priceChange: { originalCents: number; priceCents: number; reason: string } | null;
  } | null;
}

export interface ItemInput {
  title?: string;
  size?: string | null;
  category?: string | null;
  priceCents?: number;
  donate?: boolean;
  /** Einverständnis mit der Rabattaktion (X7); nur bei erlaubtem Basar. */
  discountAllowed?: boolean;
  /** Vorhandenes Etikett (C5): wird mit dem Artikel gespeichert (nur beim Anlegen). */
  externalCode?: string | null;
}

/** Antwort der Vorabprüfung vorhandener Etiketten (C5). */
export type ExternalCodeStatus = 'free' | 'taken' | 'invalid' | 'own';

/** Verkäufer in der Annahme: nur Nummer und Vorname/Kürzel, nie Nachname, E-Mail oder Telefon. */
export interface IntakeSeller {
  /** Ware abgeholt (Zeitpunkt der Bestätigung), `null`/fehlt = nicht bestätigt; fehlt in älteren gespeicherten Listen. */
  pickupConfirmedAt?: string | null;
  registrationId: string;
  sellerNumber: number;
  isHelper: boolean;
  displayName: string | null;
  itemsTotal: number;
  itemsReceived: number;
  /** Noch nicht zurückgegebene Artikel (abgegeben oder fehlt); fehlt in älteren gespeicherten Listen. */
  openItems?: number;
  /** Gebuchtes Zeitfenster für die Abgabe (X8), nur die Uhrzeit. */
  dropoffSlot?: { startsAt: string; endsAt: string } | null;
  /** Kistenzahl der Annahme (0 bis 999), `null`/fehlt = nicht erfasst. */
  crateCount?: number | null;
  /** Check-in an der Annahme, `null`/fehlt = nicht eingecheckt. */
  checkedInAt?: string | null;
}

export interface IntakeItem {
  id: string;
  itemNo: number;
  labelCodeDisplay: string;
  title: string;
  size: string | null;
  priceCents: number;
  status: 'listed' | 'received' | 'sold' | 'returned' | 'missing';
  receivedAt: string | null;
  /** Zum Artikel liegt ein Bild vor (Welle 4g); fehlt in älteren gespeicherten Listen. */
  hasImage?: boolean;
  imageVersion?: number;
}

/** Treffer der Artikelsuche (nie Nachname, E-Mail oder Telefon). */
export interface ItemSearchHit {
  id: string;
  sellerNumber: number;
  itemNo: number;
  title: string;
  size: string | null;
  priceCents: number;
  status: IntakeItem['status'];
  labelCodeDisplay: string;
  displayName: string | null;
  /** Zum Artikel liegt ein Bild vor (Welle 4g). */
  hasImage?: boolean;
  imageVersion?: number;
}

export interface ItemSearchResult {
  items: ItemSearchHit[];
  hasMore: boolean;
}

export interface ReceiveResult {
  item: IntakeItem;
  seller: IntakeSeller;
  /** Doppelter Scan: der Artikel war schon erfasst, nichts wurde geändert. */
  alreadyReceived: boolean;
}

/** Abrechnung je Verkäufer (Beträge in ganzen Cent, brutto). */
export interface SellerSettlement {
  registrationId: string;
  sellerNumber: number;
  isHelper: boolean;
  displayName: string | null;
  itemsSold: number;
  grossCents: number;
  donatedCents: number;
  baseCents: number;
  commissionCents: number;
  feeCents: number;
  payoutCents: number;
  adjustmentsCents: number;
  finalPayoutCents: number;
  receiptNo: string | null;
  paidAt: string | null;
  paidCents: number;
  /** Noch auszuzahlen (auszahlbarer Betrag minus Ausgezahltes); negativ = noch einzuziehen. */
  openCents: number;
  /** Rundungsstufe der Auszahlung (X6): 0 = keine, sonst 10, 50 oder 100 Cent. */
  roundingStepCents: number;
  /** Abgerundeter Anteil, bleibt beim Veranstalter. */
  roundingCents: number;
  /** Auszahlbarer Betrag (nach Rundung). */
  payableCents: number;
  commissionBasisPoints: number;
  /** Stufe des Helferbonus, die die Provision gesenkt hat (Beleg), sonst `null`. */
  bonusLabel: string | null;
  /** Abrechnungsregeln dieser Zeile (eingefroren nach dem Abrechnen). Der Server liefert sie immer mit. */
  minCommissionCents?: number;
  /** Die Mindestprovision hat die Provision angehoben. */
  minCommissionApplied?: boolean;
  /** Helfer-Verkäufer ohne Provision bzw. ohne Verkäufergebühr. */
  commissionExempt?: boolean;
  feeExempt?: boolean;
  /** Gebühr nur bei Umsatz: Einstellung gilt für diese Zeile / Gebühr ist entfallen (nichts verkauft). */
  feeOnlyIfSold?: boolean;
  feeWaivedNoSale?: boolean;
}

/** „Nächster Schritt“ je Basar-Phase (O8): nur Zustände und Zahlen, die Texte stehen in `lib/nextSteps.ts`. */
export interface NextSteps {
  status: EventStatus;
  steps: { key: NextStepKey; state: NextStepState; count?: number }[];
}

/**
 * Hub-Zusammenfassung (`GET /events/:eventId/hub`, U3a): Jeder Block steht nur mit dem passenden Recht in der Antwort,
 * fehlt das Recht, fehlt der Schlüssel (nie `null`). Die Oberfläche blendet Fehlendes aus und rechnet nichts nach.
 */
export interface HubSummary {
  eventId: string;
  status: EventStatus;
  phase: EventPhase;
  /** Bedingungen für Verkäufer: für alle mit Zugriff auf den Basar. */
  conditions: {
    sellerFeeCents: number;
    /** Gebühr nur bei Umsatz. */
    feeOnlyIfSold?: boolean;
    /** 1600 = 16 % */
    commissionBasisPoints: number;
    /** Mindestprovision je Verkäufer in Cent, 0 oder fehlt = keine. */
    minCommissionCents?: number;
    payoutRoundingCents: number;
    maxSellers: number | null;
    maxItemsPerSeller: number;
    discountPromosEnabled: boolean;
    scheduleSuggest?: boolean;
  };
  /** `event.sellers.approve` */
  sellers?: { applied: number; approved: number; waitlist: number; handedIn: number };
  /** `event.reports.view` */
  items?: { total: number; handedIn: number; sold: number };
  /** `event.team.manage` */
  helpers?: {
    requestsOpen: number;
    shiftSlotsFilled: number;
    shiftSlotsTotal: number;
    shiftsUnfilled: number;
    shiftRequestsOpen: number;
    teamMembers: number;
    cashiers: number;
  };
  /** `event.manage` */
  setup?: { timeSlots: number; timeSlotBookings: number; stations: number; devicesPaired: number };
  /** `event.reports.view`, nur Zahlen */
  registers?: { total: number; behind: number };
  /** `event.cash.count.any` */
  cash?: { floatMissing: number; countMissing: number };
  /** `event.manage`: Eingabe für `areaGate` (Gründe gesperrter Schritte). */
  gateSteps?: { key: NextStepKey; state: NextStepState }[];
  /** Zeitplan-Vorschlag (nur Hinweis, der Status wird nie selbst umgestellt); nur mit `event.manage` und eingeschaltetem Schalter. */
  scheduleSuggestion?: { status: string; reason: string } | null;
}

/** Eine Kasse im Live-Stand (U8a). Zeiten als ISO-Text, Beträge in ganzen Cent. */
export interface LiveRegister {
  registerId: string;
  label: string;
  /** Nur mit `event.contacts.view`, sonst `null`. */
  cashierName: string | null;
  /** Bar kassiert, auch Zeilen in Klärung (das Geld liegt in der Kasse). */
  cashSalesCents: number;
  /** Mit „Karte“ markiert (U14): nur Anzeige, nie im Bargeld. */
  cardSalesCents: number;
  salesCount: number;
  lastSyncAt: string | null;
  lastReceivedAt: string | null;
  behind: boolean;
  /** Verkäufe, die die Kasse gemeldet, der Server aber noch nicht hat. */
  waiting: number;
}

/** Live-Stand (`GET /events/:eventId/live`, U8a): Recht `event.reports.view`, `settlement` nur mit `event.settlement.run`. */
export interface LiveStand {
  eventId: string;
  status: EventStatus;
  generatedAt: string;
  revenueCents: number;
  salesCount: number;
  items: { total: number; handedIn: number; sold: number };
  /** Freigegebene Verkäufer und davon die mit angenommener Ware (Nenner der Durchschnitte je Verkäufer). */
  sellers: { approved: number; withGoods: number };
  /** „Abgeholt“: nur in den Basar-Status „Rückgabe“ und „Geschlossen“, sonst `null`. */
  pickup: { confirmed: number; total: number } | null;
  conflicts: {
    openCount: number;
    openCents: number;
    autoCountedCount: number;
    autoCountedCents: number;
  };
  /** Bar immer; Karte (U14), sobald der Basar die Markierung eingeschaltet hat oder Kartenverkäufe da sind. */
  byPaymentMethod: { method: PaymentMethod; amountCents: number }[];
  registers: LiveRegister[];
  settlement?: {
    /** `false` = vorläufig */
    finalized: boolean;
    grossCents: number;
    payableCents: number;
    organizerCents: number;
    commissionCents: number;
    feeCents: number;
    donatedCents: number;
    roundingCents: number;
    /** Provisionssatz in Basispunkten (1600 = 16 %), nur für die Prozentanzeige; nach dem Abrechnen eingefroren, `null` ohne einheitlichen Satz. */
    commissionBasisPoints: number | null;
    /** Helferbonus mit bestätigten Schichten (O5): `null`/fehlt = keine Anzeige. */
    bonusAttendance?: { confirmed: number; total: number } | null;
  };
}

/** Zahlen eines Verkäufers (`GET …/settlement/sellers`, U9): Schlüssel zur Verkäuferliste ist `registrationId`. */
export interface SellerFigure {
  registrationId: string;
  sellerNumber: number;
  isHelper: boolean;
  itemsSold: number;
  grossCents: number;
  donatedCents: number;
  commissionCents: number;
  feeCents: number;
  /** Vor der Rundung, negativ = Forderung. */
  payoutCents: number;
  adjustmentsCents: number;
  roundingCents: number;
  payableCents: number;
  /** Provision dieses Verkäufers in Basispunkten (mit Helferbonus unter der des Basars möglich). */
  commissionBasisPoints: number;
  /** Stufe, die die Provision gesenkt hat, sonst `null`. */
  bonusLabel: string | null;
  minCommissionApplied?: boolean;
  commissionExempt?: boolean;
  feeExempt?: boolean;
  /** Gebühr nur bei Umsatz: Einstellung gilt für diese Zeile / Gebühr ist entfallen (nichts verkauft). */
  feeOnlyIfSold?: boolean;
  feeWaivedNoSale?: boolean;
}

export interface SellerFigures {
  /** `false` = vorläufig */
  finalized: boolean;
  model: SettlementModel;
  commissionBasisPoints: number;
  sellerFeeCents: number;
  /** Abrechnungsregeln des Basars (Mindestprovision, Helfer-Befreiungen). */
  minCommissionCents?: number;
  helperCommissionExempt?: boolean;
  helperFeeExempt?: boolean;
  /** Verkäufergebühr nur, wenn der Verkäufer etwas verkauft hat (Standard nein = immer). */
  feeOnlyIfSold?: boolean;
  payoutRoundingCents: number;
  sellers: SellerFigure[];
  totals: { grossCents: number; payableCents: number; organizerCents: number; itemsSold: number };
  bonusAttendance?: { confirmed: number; total: number } | null;
}

/** Auswahl für den Sammeldruck: `open` = noch nicht vollständig ausgezahlt, von/bis = Verkäufernummern. */
export interface PrintSelection {
  filter: 'open' | 'all';
  from?: number;
  to?: number;
}

/** Eine Abrechnung als Druckdokument (Daten wie der PDF-Beleg des Servers). */
export interface ReceiptDocument {
  receiptNo: string;
  finalized: boolean;
  eventTitle: string;
  eventDate: string | null;
  organizationName: string;
  model: SettlementModel;
  seller: SellerSettlement;
  sellerName: string;
  /** Freiwillige Postanschrift für das Adressfeld; der Server liefert sie nur mit Recht für Kontaktdaten (bzw. die eigene). */
  sellerAddress: PostalAddressValue | null;
  items: {
    itemNo: number;
    title: string;
    priceCents: number;
    donated: boolean;
    listPriceCents: number | null;
    promoBasisPoints: number | null;
  }[];
  adjustments: { amountCents: number; reason: string; createdAt: string }[];
}

/** Antwort der Druckseite: Briefkopf der Organisation und ein Dokument je Verkäufer. */
export interface ReceiptDocuments {
  letterhead: Letterhead;
  /** Darf der Aufrufer Anschriften sehen? Nur dann ist „ohne Anschrift“ eine sinnvolle Zahl. */
  addressVisible: boolean;
  documents: ReceiptDocument[];
}

/** Rückgabeliste für die Druckseite (je Verkäufer die noch nicht verkauften Artikel, Preise nur mit Recht zur Annahme). */
/** Bedingungsblatt-Druckseite: Briefkopf, Basar, Verkäufernummer und der Zusatztext des Basars (`null` = keiner). */
export interface TermsSheetDocument {
  letterhead: Letterhead;
  eventTitle: string;
  seller: { sellerNumber: number; isHelper: boolean };
  terms: string | null;
  /** Digital unterschrieben am (Zeitpunkt), `null` = nicht digital unterschrieben. */
  signedAt?: string | null;
}

/** Verkaufsliste je Verkäufer (Druckseite): Artikel mit Häkchen für verkauft, Stand aus der Kasse. */
export interface SalesListDocument {
  letterhead: Letterhead;
  eventTitle: string;
  dateText: string | null;
  stampText: string;
  seller: { sellerNumber: number; isHelper: boolean };
  items: { itemNo: number; title: string; priceCents: number; sold: boolean }[];
}

/** Mängelzettel-Druckseite: Briefkopf, Basar und optional die Verkäufernummer einer freigegebenen Anmeldung. */
export interface DefectNoticeDocument {
  letterhead: Letterhead;
  eventTitle: string;
  seller: { sellerNumber: number; isHelper: boolean } | null;
  hasLogo: boolean;
}

/** Abholschein-Druckseite: Basar, Verkäufer (Name nur mit Recht zur Annahme) und Farbblock der Farbregel. */
export interface PickupSlipDocument {
  eventTitle: string;
  /** Ob der Briefkopf der Organisation ein Logo hat (das Bild lädt die Seite separat). */
  hasLogo: boolean;
  slips: {
    sellerNumber: number;
    isHelper: boolean;
    name: string | null;
    color: { color: ColorBlock['color']; letter: string; label: string } | null;
    /** Kistenzahl der Annahme, `null`/fehlt = nicht erfasst. */
    crateCount?: number | null;
  }[];
}

export interface ReturnListDocument {
  letterhead: Letterhead;
  eventTitle: string;
  dateText: string | null;
  showPrices: boolean;
  sellers: {
    sellerNumber: number;
    isHelper: boolean;
    /** Vorname oder Kürzel wie auf dem Etikett, ohne Recht und im Modus „nur Nummer“ `null`. */
    name: string | null;
    rows: {
      itemNo: number;
      code: string;
      title: string;
      size: string | null;
      priceCents: number | null;
    }[];
  }[];
}

/** Helferliste für die Druckseite. */
export interface HelperListDocument {
  letterhead: Letterhead;
  eventTitle: string;
  dateText: string | null;
  showContacts: boolean;
  helpers: {
    firstName: string;
    lastName: string | null;
    phone: string | null;
    role: string;
    shift: string | null;
  }[];
}

/** Schichtliste für die Druckseite (je Schicht ein Blatt). */
export interface ShiftListDocument {
  letterhead: Letterhead;
  eventTitle: string;
  dateText: string | null;
  showContacts: boolean;
  variant: PdfVariant;
  shifts: {
    name: string;
    when: string;
    location: string | null;
    description: string | null;
    occupancy: string;
    occupied: number;
    /** Leere Zeilen zum Ausfüllen (Varianten „anonym“ und „leer“). */
    emptyRows: number;
    people: {
      name: string;
      kind: 'team' | 'account' | 'external';
      waiting: boolean;
      overbooked: boolean;
      phone: string | null;
      note: string | null;
    }[];
  }[];
}

/** Auszahlungsübersicht für die Druckseite (Beträge in ganzen Cent). */
export interface PayoutListDocument {
  letterhead: Letterhead;
  eventTitle: string;
  dateText: string | null;
  finalized: boolean;
  rows: {
    sellerNumber: number;
    isHelper: boolean;
    name: string | null;
    receiptNo: string | null;
    payableCents: number;
    paidCents: number;
    openCents: number;
    paid: boolean;
  }[];
  totals: { payableCents: number; paidCents: number; openCents: number };
}

/** Verkäuferliste für die Druckseite. */
export interface SellerPrintList {
  letterhead: Letterhead;
  eventTitle: string;
  dateText: string | null;
  sellers: { sellerNumber: number; isHelper: boolean; name: string; itemsCreated: number }[];
}

export interface SettlementOverview {
  eventId: string;
  eventTitle: string;
  status: EventStatus;
  model: SettlementModel;
  finalized: boolean;
  finalizedAt: string | null;
  commissionBasisPoints: number;
  sellerFeeCents: number;
  minCommissionCents?: number;
  helperCommissionExempt?: boolean;
  helperFeeExempt?: boolean;
  /** Verkäufergebühr nur, wenn der Verkäufer etwas verkauft hat (Standard nein = immer). */
  feeOnlyIfSold?: boolean;
  payoutRoundingCents: PayoutRoundingStep;
  sellers: SellerSettlement[];
  totals: {
    grossCents: number;
    donatedCents: number;
    commissionCents: number;
    feeCents: number;
    payoutCents: number;
    adjustmentsCents: number;
    finalPayoutCents: number;
    organizerCents: number;
    roundingCents: number;
    payableCents: number;
    itemsSold: number;
  };
  issues: {
    /** Ungeklärte Konflikte (zählen nicht zur Abrechnung). */
    conflictCount: number;
    conflictCents: number;
    /** „Nicht angenommen“, automatisch mitgezählt (E14-1): Hinweis zum Prüfen. */
    autoCountedCount: number;
    autoCountedCents: number;
    quarantineCount: number;
    registersBehind: number;
    priceMismatchCount: number;
  };
  /** Kassen mit Schnellzugang (ohne Konto) mit Verkäufen (E11), nur für den Hinweis in der Abrechnung. */
  quickAccessRegisters?: number;
  /** Helferbonus mit Provision (W3-Bonus-a, O5): Helfer mit bestätigter Schicht von allen mit Schichten. `null`/fehlt: keine Anzeige. */
  bonusAttendance?: { confirmed: number; total: number } | null;
  lateSalesCount: number;
  lateSalesCents: number;
  lateVoidsCount: number;
  lateVoidsCents: number;
  /** Nachverkäufe (nach dem Kassenschluss verkauft), noch nicht in die Abrechnung übernommen. */
  afterCloseCount: number;
  afterCloseCents: number;
}

/** Trefferzeile der Konfliktsuche (nur Anzeige). */
export interface ConflictItemHit {
  itemId: string;
  itemNo: number;
  title: string;
  priceCents: number;
  sellerNumber: number | null;
  status: string;
  available: boolean;
}

/** Ergebnis der Übernahme von Nachverkäufen (je Verkäufer eine Gegenbuchung). */
export interface AfterCloseBooking {
  sellers: number;
  lines: number;
  totalCents: number;
  skippedLines: number;
}

export interface OwnSettlement extends SellerSettlement {
  eventTitle: string;
  model: SettlementModel;
}

export interface RegisterOverview {
  id: string;
  label: string;
  cashierName: string | null;
  salesCount: number;
  highestSeq: number;
  missingCount: number;
  reportedSeq: number | null;
  reportedCount: number | null;
  reportedAt: string | null;
  behind: boolean;
}

export interface Dashboard {
  itemsTotal: number;
  itemsHandedIn: number;
  itemsSold: number;
  salesCount: number;
  soldCents: number;
  conflictCount: number;
  conflictCents: number;
  autoCountedCount: number;
  autoCountedCents: number;
  priceMismatchCount: number;
  seqConflictCount: number;
  quarantineCount: number;
  registers: RegisterOverview[];
}

export interface ConflictEntry {
  lineId: string;
  kind: 'conflict' | 'price_mismatch';
  reason: string | null;
  lineNo: number;
  code: string;
  priceCents: number;
  catalogPriceCents: number | null;
  /** Erwarteter Aktionspreis (X7) bei gültiger Aktion, sonst `null`. */
  promoPriceCents?: number | null;
  /** Die Kasse meldete eine Aktion, die nicht gültig war (X7). */
  promoInvalid?: boolean;
  sellerNumber: number | null;
  registerLabel: string;
  registerSeq: number | null;
  cashierName: string | null;
  soldAt: string;
  arrivedAt: string;
  /** Klärung der Verwaltung (`counted` zählt, `excluded` zählt nicht), `null` = nicht geklärt. */
  resolution: 'counted' | 'excluded' | null;
  resolvedAt: string | null;
  /** Zählt die Zeile heute zur Abrechnung (geklärt oder automatisch mitgezählt)? */
  counted: boolean;
  /** „Nicht angenommen“, automatisch mitgezählt und noch nicht geprüft. */
  autoCounted: boolean;
  /** Mit Artikel: lässt sich als „zählt“ klären. */
  canCount: boolean;
  /** Kommentare der Kasse zu diesem Verkauf bzw. dieser Position (U17f); `author` nur mit dem Recht für Namen. */
  cashierNotes?: CashierNoteView[];
  /** Artikel ohne Etikett an der Kasse erfasst (U17h): Angaben der Kasse (ungeprüft), sonst `null`. */
  manual?: { sellerNumber: number | null; note: string | null; photoId: string | null } | null;
  /** Nutzt eine Funktion, die der Basar ausgeschaltet hat (`price_change`, `no_label`); trotzdem angenommen (Paket F). */
  settingOff?: string[];
}

export interface CashierNoteView {
  topics: string[];
  text: string | null;
  at: string;
  author: string | null;
}

/** Meldung einer Kasse („Problem melden“, U17f) in der Liste der Leitung. */
export interface RegisterNoteListEntry {
  id: string;
  registerLabel: string;
  registerSeq: number | null;
  lineNo: number | null;
  topics: string[];
  text: string | null;
  at: string;
  receivedAt: string;
  author: string | null;
  seen: boolean;
}

export type ConflictResolution = 'counted' | 'excluded';

/** Ergebnis eines importierten Verkaufs (Notausgang-Import), wie im Sync. */
export interface ImportSaleResult {
  clientUuid: string;
  status: 'accepted' | 'duplicate' | 'rejected';
  lines: { lineNo: number; result: 'sold' | 'conflict'; reason?: string }[];
}

export interface ImportResponse {
  sales: ImportSaleResult[];
  voids: { saleClientUuid: string; status: string }[];
}

export interface QuarantineEntry {
  id: string;
  clientUuid: string;
  reason: string;
  registerLabel: string;
  cashierName: string | null;
  receivedAt: string;
  totalCents: number;
  lineCount: number;
  resolvedAt: string | null;
}

/** Kennungen der Basar-Berichte, Katalog siehe `@barso/shared` (`reports.ts`). */
export type ReportKind = EventReportId;

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Spalte einer Druckansicht (wie in der Excel-Datei); `money` steht in ganzen Cent. */
export interface PrintReportColumn {
  header: string;
  width: number;
  type: 'text' | 'int' | 'money';
}

/** Blatt einer Druckansicht; die Zellen entsprechen der Excel-Datei. */
export interface PrintReportSheet {
  name: string;
  columns: PrintReportColumn[];
  rows: (string | number | null)[][];
  totals?: (string | number | null)[];
}

/** Druckansicht eines Berichts (JSON, Pfad laut Katalog). Die Web-App formatiert nur. */
export interface PrintReportDocument {
  title: string;
  infoRows: [string, string][];
  note: string;
  /** Zeitpunkt der Erstellung als Text (Berliner Zeit). */
  generatedAt: string;
  hasMoney: boolean;
  letterhead: Letterhead;
  sheets: PrintReportSheet[];
}

/** Druckansicht und Excel-Datei eines Berichts unter dem Pfad aus dem Katalog (gleiche Rechte wie der Bericht). */
export const reportPrintApi = {
  print: (path: string): Promise<PrintReportDocument> => apiRequest('GET', path),
  /** Blatt einer Kasse (Verkaufsübersicht, Bargeldabgleich, Unterschriften) der Kasse dieses Basars. */
  printRegister: (eventId: string, registerId: string): Promise<PrintReportDocument> =>
    apiRequest(
      'GET',
      `/events/${encodeURIComponent(eventId)}/registers/${encodeURIComponent(registerId)}/report/print`,
    ),
  logo: async (path: string): Promise<Blob> =>
    (await apiDownload('GET', path, undefined, 'image/png, image/jpeg')).blob,
  excel: async (path: string): Promise<Blob> =>
    (await apiDownload('GET', path, undefined, XLSX_TYPE)).blob,
};

/** Berichte aller Ebenen laut Katalog (`@barso/shared`): lädt die Datei unter dem Pfad des Eintrags. */
export const reportsApi = {
  download: async (
    report: ReportDefinition,
    ids: { orgId?: string; eventId?: string; registrationId?: string } = {},
  ): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        reportPath(report, ids),
        undefined,
        report.format === 'pdf' ? 'application/pdf' : XLSX_TYPE,
      )
    ).blob,
};

/** Eine Zeile des Bargeldabgleichs (X5): Wechselgeld, Entnahme oder Zählung einer Kasse. */
export interface CashEntry {
  id: string;
  kind: CashEntryKind;
  amountCents: number;
  breakdown: Record<string, number> | null;
  countedAt: string;
  note: string | null;
  supersedesId: string | null;
  /** Durch eine neuere Zeile ersetzt (nur noch Verlauf). */
  superseded: boolean;
  createdAt: string;
  /** Nur mit dem Recht für Kontaktdaten. */
  createdByName: string | null;
  /** Nur bei Zählungen: erwartet zum Zählzeitpunkt (aus dem aktuellen Stand berechnet). */
  expectedCents: number | null;
  differenceCents: number | null;
  provisional: boolean;
}

export interface CashRegister {
  registerId: string;
  label: string;
  /** Kassenplatz bei gekoppelten Gerätekassen (Gruppierung), sonst `null`. */
  stationLabel: string | null;
  floatCents: number;
  withdrawalCents: number;
  cashSalesCents: number;
  /** Mit „Karte“ markiert (U14): nur Anzeige, nie im Bargeld. */
  cardSalesCents: number;
  behind: boolean;
  counts: CashEntry[];
  latest: {
    expectedCents: number;
    countedCents: number | null;
    differenceCents: number | null;
    countedAt: string | null;
    provisional: boolean;
  };
}

export interface CashEntryInput {
  kind: CashEntryKind;
  amountCents?: number;
  breakdown?: Record<string, number>;
  /** ISO-Zeitpunkt, höchstens 48 Stunden zurück. */
  countedAt?: string;
  note?: string;
  supersedesId?: string;
}

/** Bargeldabgleich je Kasse (X5): Kassierer sehen nur die eigene Kasse, die Verwaltung alle. */
export const cashApi = {
  overview: (eventId: string) =>
    apiRequest<{ registers: CashRegister[] }>('GET', `/events/${eventId}/cash-counts`),
  record: (eventId: string, registerId: string, input: CashEntryInput) =>
    apiRequest<CashEntry>('POST', `/events/${eventId}/registers/${registerId}/cash-counts`, input),
};

/** Auswertungen mit Diagrammen (Plan 12): ein Basar mit allen Abschnitten, Vergleich mehrerer Basare einer Organisation. */
export const analyticsApi = {
  event: (eventId: string) => apiRequest<EventAnalytics>('GET', `/events/${eventId}/analytics`),
  /** Ohne Auswahl: die letzten acht Basare der Organisation. */
  compare: (orgId: string, eventIds: readonly string[] = []) =>
    apiRequest<EventComparison>(
      'GET',
      `/orgs/${orgId}/analytics/compare${
        eventIds.length === 0 ? '' : `?events=${eventIds.map(encodeURIComponent).join(',')}`
      }`,
    ),
};

/** Rabattaktion (X7): eine Aktion eines Basars. */
export interface PromoEntry {
  id: string;
  basisPoints: number;
  startsAt: string;
  endsAt: string | null;
  /** Regel „letzte x Minuten“: Vorlauf vor dem Verkaufsende, `null` bei festem Zeitraum. */
  leadMinutes: number | null;
  cancelledAt: string | null;
  createdAt: string;
  state: 'scheduled' | 'active' | 'ended' | 'cancelled';
}

/** Vorlage „letzte x Minuten“ der Organisation (wiederverwendbar je Basar). */
export interface PromoTemplate {
  id: string;
  name: string;
  basisPoints: number;
  leadMinutes: number;
  createdAt: string;
}

export interface PromoOverview {
  /** Der Basar erlaubt Aktionen. */
  enabled: boolean;
  /** Artikel, deren Verkäufer die Reduzierung erlaubt haben. */
  allowedItems: number;
  itemsTotal: number;
  promos: PromoEntry[];
}

/** Rabattaktionen des Veranstalters (X7, Recht `event.manage`). */
export const promoApi = {
  list: (eventId: string) => apiRequest<PromoOverview>('GET', `/events/${eventId}/discount-promos`),
  create: (
    eventId: string,
    input:
      | { basisPoints: number; startsAt: string; endsAt?: string | null }
      | { basisPoints: number; leadMinutes: number }
      | { templateId: string },
  ) => apiRequest<PromoEntry>('POST', `/events/${eventId}/discount-promos`, input),
  cancel: (eventId: string, promoId: string) =>
    apiRequest<PromoEntry>('POST', `/events/${eventId}/discount-promos/${promoId}/cancel`),
  templates: (eventId: string) =>
    apiRequest<PromoTemplate[]>('GET', `/events/${eventId}/promo-templates`),
  createTemplate: (
    eventId: string,
    input: { name: string; basisPoints: number; leadMinutes: number },
  ) => apiRequest<PromoTemplate>('POST', `/events/${eventId}/promo-templates`, input),
  deleteTemplate: (eventId: string, templateId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/promo-templates/${templateId}`),
};

/** Übersicht, Abrechnung und Auswertungen des Veranstalters. */
/** Eintrag der Verkaufsübersicht für Sonderfälle (U17i): Artikel ohne Etikett, Preiskorrekturen, Stornos mit Grund. */
export interface SaleAdjustmentEntry {
  lineId: string;
  registerLabel: string;
  registerSeq: number | null;
  lineNo: number;
  soldAt: string;
  priceCents: number;
  cashierName: string | null;
  manual: {
    sellerNumber: number | null;
    note: string | null;
    photoId: string | null;
    photoShownToSeller: boolean | null;
  } | null;
  assigned: { sellerNumber: number | null; itemId: string; itemTitle: string; at: string } | null;
  priceChange: { originalCents: number; reason: string; note: string | null } | null;
  voided: { reason: string | null; note: string | null; at: string } | null;
  resolution: 'counted' | 'excluded' | null;
  cashierNotes: CashierNoteView[];
  /** Nutzt eine Funktion, die der Basar ausgeschaltet hat (`price_change`, `no_label`); trotzdem angenommen (Paket F). */
  settingOff?: string[];
}

export interface AssignBody {
  sellerNumber?: number;
  itemId?: string;
  newItem?: { title: string };
}

/** Auszahlungsart je Anmeldung (W3-Auszahlung): bar oder Überweisung. */
export type PayoutMethod = 'cash' | 'transfer';

/** Bankverbindung (Profil oder Konto des Vereins), nie mit der vollen IBAN. */
export interface BankAccountView {
  /** Läuft die Funktion auf diesem System (Schlüssel für Bankverbindungen gesetzt)? */
  available: boolean;
  configured: boolean;
  ibanMask: string | null;
  holder: string | null;
  /** Gespeichert, aber nicht mehr lesbar: Bitte neu eingeben. */
  unreadable: boolean;
  updatedAt: string | null;
}

export interface BankAccountInput {
  iban: string;
  holder: string;
  password: string;
  code?: string;
}

export type TransferState =
  'not_settled' | 'ready' | 'missing_iban' | 'unreadable' | 'zero' | 'paid';

export interface TransferEntry {
  registrationId: string;
  sellerNumber: number;
  displayName: string | null;
  ibanMask: string | null;
  /** Offener auszahlbarer Betrag in Cent, `null` vor dem Abrechnen. */
  amountCents: number | null;
  state: TransferState;
}

export interface TransfersView {
  finalized: boolean;
  entries: TransferEntry[];
  readyCount: number;
  readyCents: number;
  orgAccount: { configured: boolean; ibanMask: string | null; unreadable: boolean };
  lastSepaExportAt: string | null;
}

export interface GiroCodeView {
  /** Inhalt des QR-Codes (EPC069-12). */
  text: string;
  amountCents: number;
  sellerNumber: number;
  holder: string;
  ibanMask: string;
  remittance: string;
}

/** Bankverbindung, Auszahlungsart und Überweisungsdaten (W3-Auszahlung). BARSO überweist nichts, es erzeugt nur Codes und Dateien. */
export const payoutApi = {
  bankAccount: () => apiRequest<BankAccountView>('GET', '/me/bank-account'),
  saveBankAccount: (input: BankAccountInput) =>
    apiRequest<BankAccountView>('PUT', '/me/bank-account', input),
  deleteBankAccount: () => apiRequest<undefined>('DELETE', '/me/bank-account'),
  setMethod: (registrationId: string, payoutMethod: PayoutMethod) =>
    apiRequest<{ registrationId: string; payoutMethod: PayoutMethod }>(
      'PATCH',
      `/me/registrations/${registrationId}/payout-method`,
      { payoutMethod },
    ),
  orgAccount: (orgId: string) =>
    apiRequest<BankAccountView>('GET', `/orgs/${orgId}/payout-account`),
  saveOrgAccount: (orgId: string, input: BankAccountInput) =>
    apiRequest<BankAccountView>('PUT', `/orgs/${orgId}/payout-account`, input),
  deleteOrgAccount: (orgId: string, input: { password: string; code?: string }) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/payout-account`, input),
  transfers: (eventId: string) =>
    apiRequest<TransfersView>('GET', `/events/${eventId}/settlement/transfers`),
  giroCode: (eventId: string, registrationId: string) =>
    apiRequest<GiroCodeView>(
      'GET',
      `/events/${eventId}/settlement/sellers/${registrationId}/girocode`,
    ),
  /** SEPA-Sammeldatei (pain.001.001.09); `date` = Ausführungsdatum, ohne Angabe der nächste Bankarbeitstag. */
  sepaFile: async (
    eventId: string,
    date?: string,
  ): Promise<{ blob: Blob; count: number | null; totalCents: number | null }> => {
    const { blob, headers } = await apiDownload(
      'GET',
      `/events/${eventId}/settlement/sepa.xml${date === undefined || date === '' ? '' : `?date=${encodeURIComponent(date)}`}`,
      undefined,
      'application/xml',
    );
    const number = (name: string): number | null => {
      const value = headers.get(name);
      return value !== null && /^\d+$/.test(value) ? Number(value) : null;
    };
    return { blob, count: number('X-Transfer-Count'), totalCents: number('X-Transfer-Cents') };
  },
  /** Alle Zeilen der Datei als ausgezahlt abhaken: genau die Beträge, die die Datei enthielt. */
  markPaid: (eventId: string, transfers: { registrationId: string; cents: number }[]) =>
    apiRequest<{ count: number; totalCents: number }>(
      'POST',
      `/events/${eventId}/settlement/transfers/mark-paid`,
      transfers,
    ),
};

export const settlementApi = {
  adjustments: (eventId: string) =>
    apiRequest<SaleAdjustmentEntry[]>('GET', `/events/${eventId}/sale-adjustments`),
  assignLine: (eventId: string, lineId: string, body: AssignBody) =>
    apiRequest<{ lineId: string; itemId: string; sellerNumber: number | null; newItem: boolean }>(
      'POST',
      `/events/${eventId}/sale-lines/${lineId}/assign`,
      body,
    ),
  unassignLine: (eventId: string, lineId: string) =>
    apiRequest<{ lineId: string }>('POST', `/events/${eventId}/sale-lines/${lineId}/unassign`),
  salePhoto: async (eventId: string, photoId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/sale-photos/${photoId}`, undefined, 'image/jpeg'))
      .blob,
  setSalePhotoVisibility: (eventId: string, photoId: string, showToSeller: boolean) =>
    apiRequest<{ id: string; showToSeller: boolean }>(
      'POST',
      `/events/${eventId}/sale-photos/${photoId}/visibility`,
      { showToSeller },
    ),
  deleteSalePhoto: (eventId: string, photoId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/sale-photos/${photoId}`),
  dashboard: (eventId: string) => apiRequest<Dashboard>('GET', `/events/${eventId}/dashboard`),
  conflicts: (eventId: string) =>
    apiRequest<ConflictEntry[]>('GET', `/events/${eventId}/conflicts`),
  quarantine: (eventId: string) =>
    apiRequest<QuarantineEntry[]>('GET', `/events/${eventId}/quarantine`),
  registerNotes: (eventId: string) =>
    apiRequest<RegisterNoteListEntry[]>('GET', `/events/${eventId}/register-notes`),
  markNoteSeen: (eventId: string, noteId: string) =>
    apiRequest<{ id: string; seen: true }>(
      'POST',
      `/events/${eventId}/register-notes/${noteId}/seen`,
    ),
  resolveConflict: (eventId: string, lineId: string, resolution: ConflictResolution) =>
    apiRequest<{ lineId: string; resolution: ConflictResolution; counted: boolean }>(
      'POST',
      `/events/${eventId}/conflicts/${lineId}/resolve`,
      { resolution },
    ),
  resolveQuarantine: (eventId: string, quarantineId: string) =>
    apiRequest<{ id: string; resolvedAt: string }>(
      'POST',
      `/events/${eventId}/quarantine/${quarantineId}/resolve`,
    ),
  /** Notausgang-Import: ein Stapel (höchstens 50 Verkäufe bzw. Stornos) einer Kasse. */
  importRegister: (
    eventId: string,
    registerId: string,
    body: { sales: unknown[]; voids: unknown[] },
  ) =>
    // Wie der Sync über `client_uuid` idempotent (Wiederholen bucht nichts doppelt)
    apiRequest<ImportResponse>('POST', `/events/${eventId}/registers/${registerId}/import`, body, {
      timeoutMs: TIMEOUT_LONG_MS,
      idempotent: true,
    }),
  overview: (eventId: string) =>
    apiRequest<SettlementOverview>('GET', `/events/${eventId}/settlement`),
  nextSteps: (eventId: string) => apiRequest<NextSteps>('GET', `/events/${eventId}/next-steps`),
  /** Live-Stand (U8a): Umsatz, Artikel, Kassen; mit Abrechnungsrecht auch Auszahlung und Anteil. */
  live: (eventId: string) => apiRequest<LiveStand>('GET', `/events/${eventId}/live`),
  /** Zahlen je Verkäufer für die Verkäuferliste (U9), nur mit `event.settlement.run`. */
  sellerFigures: (eventId: string) =>
    apiRequest<SellerFigures>('GET', `/events/${eventId}/settlement/sellers`),
  finalize: (eventId: string, acknowledgeOpenIssues: boolean) =>
    apiRequest<SettlementOverview>(
      'POST',
      `/events/${eventId}/settlement/finalize`,
      { acknowledgeOpenIssues },
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  /** Teilsuche für die Konfliktliste (Welle 4b-1): Verkäufernummer, Artikelnummer, Preis oder Text. */
  searchConflictItems: (eventId: string, q: string) =>
    apiRequest<ConflictItemHit[]>(
      'GET',
      `/events/${eventId}/conflicts/item-search?q=${encodeURIComponent(q)}`,
    ),
  /** Konflikt mit Code einem gefundenen Artikel zuordnen; danach zählt die Zeile (Welle 4b-1). */
  assignConflict: (eventId: string, lineId: string, itemId: string) =>
    apiRequest<{ lineId: string; itemId: string; sellerNumber: number | null; counted: true }>(
      'POST',
      `/events/${eventId}/conflicts/${lineId}/assign`,
      { itemId },
    ),
  bookAfterClose: (eventId: string) =>
    apiRequest<AfterCloseBooking>('POST', `/events/${eventId}/settlement/after-close/book`, {}),
  addAdjustment: (
    eventId: string,
    registrationId: string,
    input: { amountCents: number; reason: string },
  ) =>
    apiRequest<undefined>(
      'POST',
      `/events/${eventId}/settlement/sellers/${registrationId}/adjustments`,
      input,
    ),
  setPaid: (eventId: string, registrationId: string, paid: boolean) =>
    apiRequest<undefined>('PATCH', `/events/${eventId}/settlement/sellers/${registrationId}/paid`, {
      paid,
    }),
  /** Auszahlungsquittung als PDF (erst nach dem Abrechnen). */
  payoutReceiptPdf: async (eventId: string, registrationId: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/settlement/sellers/${registrationId}/payout-receipt.pdf`,
      )
    ).blob,
  receiptPdf: async (eventId: string, registrationId: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/settlement/sellers/${registrationId}/receipt.pdf`,
      )
    ).blob,
  /** Sammeldruck (O7): alle Belege oder Umschlag-Etiketten in einer Datei, nur nach dem Abrechnen. */
  printPdf: async (
    eventId: string,
    kind: 'receipts' | 'envelopes',
    selection: PrintSelection,
  ): Promise<Blob> => {
    const query = new URLSearchParams({ filter: selection.filter });
    if (selection.from !== undefined) query.set('from', String(selection.from));
    if (selection.to !== undefined) query.set('to', String(selection.to));
    return (
      await apiDownload('GET', `/events/${eventId}/settlement/${kind}.pdf?${query.toString()}`)
    ).blob;
  },
  /** Druckseite (Massendruck): ein Dokument je Verkäufer, mit Audit-Eintrag wie beim Sammel-PDF. */
  receiptDocuments: (eventId: string, selection: PrintSelection) => {
    const query = new URLSearchParams({ filter: selection.filter });
    if (selection.from !== undefined) query.set('from', String(selection.from));
    if (selection.to !== undefined) query.set('to', String(selection.to));
    return apiRequest<ReceiptDocuments>(
      'GET',
      `/events/${eventId}/settlement/receipts?${query.toString()}`,
      undefined,
      { timeoutMs: TIMEOUT_LONG_MS },
    );
  },
  /** Druckseite (Einzeldruck eines Verkäufers, auch vorläufig als Entwurf). */
  receiptDocument: (eventId: string, registrationId: string) =>
    apiRequest<ReceiptDocuments>(
      'GET',
      `/events/${eventId}/settlement/sellers/${registrationId}/receipt`,
    ),
  /** Druckseite der Auszahlungsübersicht (wie die Excel-Auszahlungsliste, dazu der Briefkopf). */
  payoutList: (eventId: string) =>
    apiRequest<PayoutListDocument>('GET', `/events/${eventId}/settlement/payout-list`),
  /** Briefkopf-Logo der Organisation des Basars (Bild mit Anmeldung laden), ohne Logo 404. */
  eventLetterheadLogo: async (eventId: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/letterhead/logo`,
        undefined,
        'image/png, image/jpeg',
      )
    ).blob,
  ownReceiptDocument: (registrationId: string) =>
    apiRequest<ReceiptDocuments>('GET', `/me/registrations/${registrationId}/settlement/receipt`),
  ownLetterheadLogo: async (registrationId: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/me/registrations/${registrationId}/settlement/letterhead-logo`,
        undefined,
        'image/png, image/jpeg',
      )
    ).blob,
  report: async (eventId: string, report: ReportKind): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/reports/${report}/xlsx`, undefined, XLSX_TYPE))
      .blob,
  own: (registrationId: string) =>
    apiRequest<OwnSettlement>('GET', `/me/registrations/${registrationId}/settlement`),
  /** Vorläufige eigene Auszahlung vor der Abrechnung (Welle 4); 404, wenn der Veranstalter den Verkaufsstand verbirgt. */
  ownLive: (registrationId: string) =>
    apiRequest<OwnSettlement>('GET', `/me/registrations/${registrationId}/settlement/live`),
  ownReceiptPdf: async (registrationId: string): Promise<Blob> =>
    (await apiDownload('GET', `/me/registrations/${registrationId}/settlement/receipt.pdf`)).blob,
};

const sessionHeaders = (sessionToken: string | undefined): { headers?: Record<string, string> } =>
  sessionToken === undefined ? {} : { headers: { 'X-Register-Session': sessionToken } };

/** Zeitlimit je Paket des Kassen-Syncs (SYN-04: 5 bis 8 s plus Puffer). Der Sync ist über `client_uuid` idempotent. */
const SYNC_LIMITS = { timeoutMs: 10_000, idempotent: true } as const;
/** Zeitlimit der Scan-Aufrufe von Annahme und Rückgabe (idempotent: „Schon erfasst“). */
const SCAN_LIMITS = { timeoutMs: 8_000, idempotent: true } as const;

/**
 * Gerätesitzung des Schnellzugangs (E11): nur der Kopf `X-Register-Device`, nie ein Zugriffstoken, kein automatisches
 * Erneuern und keine Abmeldung der Oberfläche. Eine 401-Antwort heißt: Das Gerät wurde abgekoppelt oder gesperrt (oder die
 * Sitzung ist abgelaufen). Die Kasse sperrt dann den Verkauf, die Warteschlange bleibt erhalten.
 */
const deviceOptions = (
  deviceToken: string,
  limits: { timeoutMs?: number; idempotent?: boolean } = {},
): { auth: false; headers: Record<string, string>; timeoutMs?: number; idempotent?: boolean } => ({
  auth: false,
  headers: { 'X-Register-Device': deviceToken },
  ...limits,
});

async function asDevice<T>(work: () => Promise<T>): Promise<T> {
  try {
    return await work();
  } catch (caught) {
    if (caught instanceof ApiClientError && caught.status === 401) {
      throw new ApiClientError(
        401,
        'DEVICE_REVOKED',
        'Dieses Gerät wurde abgekoppelt oder gesperrt. Ihre Verkäufe bleiben gespeichert.',
      );
    }
    throw caught;
  }
}

/** Rolle, die ein Einladungslink vergibt (Helfer, Kasse, Warenannahme). */
export type InviteRole = 'helper' | 'cashier' | 'intake';

export const INVITE_ROLE_LABELS: Record<InviteRole, string> = {
  helper: 'Helfer',
  cashier: 'Kasse',
  intake: 'Warenannahme',
};

/** Rolle in „Meine Einsätze“ und „Ihre Kassen“: zusätzlich der Manager des Basars (kein Einladungslink dafür). */
export type HelperEventRole = InviteRole | 'manager';

export const HELPER_EVENT_ROLE_LABELS: Record<HelperEventRole, string> = {
  ...INVITE_ROLE_LABELS,
  manager: 'Manager',
};

/** Einladungslink für Helfer (HLP-01/02). */
export interface HelperInvite {
  id: string;
  mode: 'approval' | 'auto';
  role: InviteRole;
  /** Der Link lässt sich erneut anzeigen und als Aushang drucken (alte Links nicht). */
  reshowable: boolean;
  expiresAt: string;
  maxUses: number | null;
  uses: number;
  revokedAt: string | null;
  createdAt: string;
  active: boolean;
}

export type HelperStatus = 'requested' | 'approved' | 'rejected' | 'removed';

export interface HelperEntry {
  requestId: string;
  userId: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  status: HelperStatus;
  role: InviteRole;
  requestedAt: string;
  /** Anwesenheit am Basartag, `null` = nicht vermerkt. */
  checkedInAt: string | null;
  shiftId: string | null;
  shiftName: string | null;
}

export type ShiftSignupMode = 'team' | 'accounts' | 'public';

/** Helferschicht mit Zeit, Ort, Plätzen und Belegung (Plan Nr. 41). */
export interface HelperShift {
  id: string;
  name: string;
  startsAt: string | null;
  endsAt: string | null;
  location: string | null;
  description: string | null;
  /** `null` = unbegrenzt */
  capacity: number | null;
  signupMode: ShiftSignupMode;
  waitlistEnabled: boolean;
  createdAt: string;
  signupCount: number;
  teamCount: number;
  occupied: number;
  waitingCount: number;
  /** Noch freie Plätze, `null` = unbegrenzt. */
  openSlots: number | null;
  /** Anwesende unter den aktiven Eintragungen und Team-Helfern („3/5 da“). */
  checkedInCount: number;
  /** Anfragen, die auf eine Entscheidung des Teams warten (Plan Nr. 13). */
  openRequestCount: number;
}

export type ShiftRequestType = 'cancel' | 'offer' | 'swap';
export type ShiftRequestStatus =
  'open' | 'pending' | 'done' | 'rejected' | 'withdrawn' | 'expired' | 'void';
export type CancelMode = 'free' | 'reason' | 'approval' | 'blocked';

export interface ShiftPerson {
  id: string;
  kind: 'team' | 'account' | 'external';
  firstName: string;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  note: string | null;
  status: 'active' | 'waitlisted';
  overbooked: boolean;
  position: number | null;
  createdAt: string;
  checkedInAt: string | null;
  /** Offene oder abgelaufene Anfrage dieser Person (ohne Begründung). */
  request: { id: string; type: ShiftRequestType; status: ShiftRequestStatus; late: boolean } | null;
}

export interface ShiftOverview extends HelperShift {
  people: ShiftPerson[];
}

export interface ShiftInput {
  name: string;
  startsAt: string;
  endsAt: string;
  location: string | null;
  description: string | null;
  capacity: number | null;
  signupMode: ShiftSignupMode;
  waitlistEnabled: boolean;
}

export interface OpenShift {
  id: string;
  name: string;
  startsAt: string;
  endsAt: string;
  location: string | null;
  description: string | null;
  /** freie Plätze, `null` = unbegrenzt */
  free: number | null;
  waiting: number;
  state: 'open' | 'waitlist' | 'full';
}

export type SignupRelease = 'open' | 'closed' | 'scheduled';

export type CancelPolicy = 'free' | 'reason' | 'approval';
export type LatePolicy = 'approval' | 'blocked';
export type SwapPolicy = 'off' | 'direct' | 'approval';

/** Regeln für Absage, Tausch und Platz abgeben je Basar (Plan Nr. 13). */
export interface RequestPolicy {
  cancelPolicy: CancelPolicy;
  /** Frist in Stunden vor Beginn, `null` = keine Frist. */
  cancelLateHours: number | null;
  cancelLatePolicy: LatePolicy;
  swapPolicy: SwapPolicy;
  offerEnabled: boolean;
  offerNeedsApproval: boolean;
}

export interface ShiftSettings extends RequestPolicy {
  signupRelease: SignupRelease;
  signupOpensAt: string | null;
  signupOpen: boolean;
  /** Der einmalige Hinweis auf die neuen Regeln ist noch nicht quittiert. */
  noticePending: boolean;
}

/** Eigene Anfrage (Absage, Angebot, Tausch) mit der Antwort des Teams: nur für die Person selbst. */
export interface OwnShiftRequest {
  id: string;
  type: ShiftRequestType;
  status: ShiftRequestStatus;
  late: boolean;
  hasResponse: boolean;
  response: string | null;
  targetShiftName: string | null;
  /** Das Team hat entschieden (genehmigt). */
  approved: boolean;
}

export interface PersonRef {
  firstName: string;
  lastName: string | null;
  kind: 'account' | 'external';
}
export interface ShiftRef {
  id: string;
  name: string;
  startsAt: string | null;
  endsAt: string | null;
}

/** Anfrage in der Liste des Teams (mit Begründung und Antwort). */
export interface ShiftRequestView {
  id: string;
  type: ShiftRequestType;
  status: ShiftRequestStatus;
  late: boolean;
  outcome: string | null;
  shift: ShiftRef;
  targetShift: ShiftRef | null;
  requester: PersonRef;
  counterpart: PersonRef | null;
  reason: string | null;
  response: string | null;
  createdAt: string;
  decidedAt: string | null;
  decidedByName: string | null;
  shiftOccupied: number;
  shiftCapacity: number | null;
  shiftWaiting: number;
}

/** Angebot für mich: ein Platz zum Übernehmen oder ein Tausch (ohne Namen und Begründung). */
export interface MarketEntry {
  id: string;
  type: 'offer' | 'swap';
  shift: ShiftRef & { location: string | null };
  /** Beim Tausch die eigene Schicht, die man abgäbe. */
  targetShift: (ShiftRef & { location: string | null }) | null;
  needsApproval: boolean;
  createdAt: string;
}

export interface OpenShiftForAccount extends OpenShift {
  signupOpen: boolean;
  signupOpensAt: string | null;
  signupMode: ShiftSignupMode;
  mine: { status: 'active' | 'waitlisted' | 'team'; position: number | null } | null;
}

export interface MyShift {
  signupId: string;
  eventId: string;
  eventTitle: string;
  organizationName: string;
  shiftId: string;
  shiftName: string;
  startsAt: string | null;
  endsAt: string | null;
  location: string | null;
  description: string | null;
  /** `team`: vom Team eingeteilt (Austragen nur durch das Team). `ended`: genehmigte Anfrage der letzten 14 Tage (nur Anzeige). */
  status: 'active' | 'waitlisted' | 'team' | 'ended';
  position: number | null;
  request: OwnShiftRequest | null;
  /** Was jetzt möglich ist; `null` bei Zuteilung durch das Team. */
  policy: { cancelMode: CancelMode; offerAllowed: boolean; swapAllowed: boolean } | null;
  /** Hinweis beim Absagen: Die Höchstzahl der Artikel sinkt (Helferbonus). Fehlt/`null` ohne Wirkung. */
  bonusHint?: string | null;
}

export interface ShiftLink {
  id: string;
  url: string;
  expiresAt: string;
  createdAt: string;
}

export interface PublicShiftSignup {
  token: string;
  shiftId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note: string | null;
  privacyAck: true;
}

export interface HelperEvent {
  eventId: string;
  title: string;
  organizationName: string;
  status: EventStatus;
  startsAt: string | null;
  closesAt: string | null;
  endsAt: string | null;
  /** Rollen der Person in diesem Basar: bestimmen die Knöpfe. */
  roles: HelperEventRole[];
  /** Zusatzfunktion Laufzettel eingeschaltet (fehlt bei älterem Server: aus). */
  runSheetEnabled?: boolean;
  /** Zusatzfunktion Helferbonus eingeschaltet: Station „Einlass“ (fehlt bei älterem Server: aus). */
  helperBonusEnabled?: boolean;
  /** Eigene Schichten in diesem Basar (Zuteilung und Eintragung mit Konto). */
  shifts: {
    shiftId: string;
    name: string;
    startsAt: string | null;
    endsAt: string | null;
    location: string | null;
  }[];
}

/** Helferschichten (Plan Nr. 41): Verwaltung, Schicht-Link, Eintragen mit Konto und ohne Konto. */
export const shiftApi = {
  list: (eventId: string) => apiRequest<HelperShift[]>('GET', `/events/${eventId}/helper-shifts`),
  overview: (eventId: string) =>
    apiRequest<ShiftOverview[]>('GET', `/events/${eventId}/helper-shifts/overview`),
  create: (eventId: string, input: ShiftInput) =>
    apiRequest<HelperShift>('POST', `/events/${eventId}/helper-shifts`, input),
  update: (eventId: string, shiftId: string, input: Partial<ShiftInput>) =>
    apiRequest<HelperShift>('PATCH', `/events/${eventId}/helper-shifts/${shiftId}`, input),
  remove: (eventId: string, shiftId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/helper-shifts/${shiftId}`),
  removeSignup: (eventId: string, shiftId: string, signupId: string) =>
    apiRequest<undefined>(
      'DELETE',
      `/events/${eventId}/helper-shifts/${shiftId}/signups/${signupId}`,
    ),
  promote: (eventId: string, shiftId: string, signupId: string) =>
    apiRequest<undefined>(
      'POST',
      `/events/${eventId}/helper-shifts/${shiftId}/signups/${signupId}/promote`,
    ),
  /** Druckliste aller Schichten oder einer Schicht (mit Telefon nur für Berechtigte, entscheidet der Server). */
  listPdf: async (eventId: string, shiftId?: string, variant?: PdfVariant): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/helper-shifts/list.pdf${pdfQuery(shiftId, variant)}`,
      )
    ).blob,
  /** Druckseite der Schichtliste (je Schicht ein Blatt): dieselben Daten und Rechte wie das PDF, dazu der Briefkopf. */
  listDocument: (eventId: string, shiftId?: string, variant?: PdfVariant) =>
    apiRequest<ShiftListDocument>(
      'GET',
      `/events/${eventId}/helper-shifts/list${pdfQuery(shiftId, variant)}`,
    ),
  /** Anwesenheit am Basartag: Haken für eine Eintragung (Konto oder extern). */
  setCheckIn: (eventId: string, shiftId: string, signupId: string, present: boolean) =>
    apiRequest<undefined>(
      present ? 'POST' : 'DELETE',
      `/events/${eventId}/helper-shifts/${shiftId}/signups/${signupId}/check-in`,
    ),
  /** Schichtplan zum Aushängen: nur „Vorname N.“, Zeit, Ort und freie Plätze. */
  planPdf: async (eventId: string, variant?: PdfVariant, shiftId?: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/helper-shifts/plan.pdf${pdfQuery(shiftId, variant)}`,
      )
    ).blob,
  /** Aushang (A4) mit QR-Code zum Schicht-Link; das Token wird serverseitig eingesetzt. */
  linkPosterPdf: async (eventId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/helper-shift-link/poster.pdf`)).blob,
  getSettings: (eventId: string) =>
    apiRequest<ShiftSettings>('GET', `/events/${eventId}/helper-shift-settings`),
  /** Teil-Update: fehlende Felder bleiben unverändert. */
  setSettings: (
    eventId: string,
    input: Partial<RequestPolicy> & {
      signupRelease?: SignupRelease;
      signupOpensAt?: string | null;
      noticeAck?: boolean;
    },
  ) => apiRequest<ShiftSettings>('PUT', `/events/${eventId}/helper-shift-settings`, input),
  listRequests: (eventId: string, status: 'open' | 'all' = 'open') =>
    apiRequest<ShiftRequestView[]>(
      'GET',
      `/events/${eventId}/helper-shift-requests?status=${status}`,
    ),
  decideRequest: (
    eventId: string,
    requestId: string,
    decision: 'approve' | 'reject',
    response: string | null,
  ) =>
    apiRequest<undefined>(
      'POST',
      `/events/${eventId}/helper-shift-requests/${requestId}/${decision}`,
      response === null ? {} : { response },
    ),
  getLink: (eventId: string) =>
    apiRequest<ShiftLink>('GET', `/events/${eventId}/helper-shift-link`),
  createLink: (eventId: string) =>
    apiRequest<ShiftLink>('POST', `/events/${eventId}/helper-shift-link`),
  revokeLink: (eventId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/helper-shift-link`),
  open: (eventId: string) =>
    apiRequest<OpenShiftForAccount[]>('GET', `/events/${eventId}/helper-shifts/open`),
  /** „Schichten sind frei“ (Welle 4j): Vorschau nur mit Zahlen, dann Versand. */
  callPreview: (eventId: string) =>
    apiRequest<ShiftCallPreview>('GET', `/events/${eventId}/helper-shift-call/preview`),
  callSend: (eventId: string) =>
    apiRequest<{ sent: number }>('POST', `/events/${eventId}/helper-shift-call`),
  signup: (
    eventId: string,
    shiftId: string,
    input: { phone: string | null; note: string | null },
  ) =>
    apiRequest<{ status: 'active' | 'waitlisted' | 'team'; position: number | null }>(
      'POST',
      `/events/${eventId}/helper-shifts/${shiftId}/signup`,
      input,
    ),
  /** Absage mit Begründung: sofort wirksam (`cancelled`) oder als Anfrage an das Team (`requested`). */
  cancelOwn: (eventId: string, shiftId: string, reason: string | null) =>
    apiRequest<{ result: 'cancelled' | 'requested' }>(
      'POST',
      `/events/${eventId}/helper-shifts/${shiftId}/cancel`,
      reason === null ? {} : { reason },
    ),
  offerShift: (eventId: string, shiftId: string) =>
    apiRequest<{ result: 'handed_over' | 'offered' }>(
      'POST',
      `/events/${eventId}/helper-shifts/${shiftId}/offer`,
      {},
    ),
  swapShift: (eventId: string, shiftId: string, targetShiftId: string) =>
    apiRequest<{ result: 'offered' }>('POST', `/events/${eventId}/helper-shifts/${shiftId}/swap`, {
      targetShiftId,
    }),
  market: (eventId: string) =>
    apiRequest<MarketEntry[]>('GET', `/events/${eventId}/helper-shift-market`),
  acceptRequest: (eventId: string, requestId: string) =>
    apiRequest<{ result: 'done' | 'pending' }>(
      'POST',
      `/events/${eventId}/helper-shift-requests/${requestId}/accept`,
      {},
    ),
  withdrawRequest: (eventId: string, requestId: string) =>
    apiRequest<undefined>(
      'POST',
      `/events/${eventId}/helper-shift-requests/${requestId}/withdraw`,
      {},
    ),
  mine: () => apiRequest<MyShift[]>('GET', '/me/helper-shifts'),
  // Ohne Konto: das Token aus dem Link (bzw. Abmelde-Link) ist das Geheimnis
  publicPreview: (token: string) =>
    apiRequest<{
      eventTitle: string;
      organizationName: string;
      signupOpen: boolean;
      signupOpensAt: string | null;
      shifts: OpenShift[];
    }>('POST', '/public/helper-shifts/preview', { token }, publicRoute),
  publicSignup: (input: PublicShiftSignup) =>
    apiRequest<{ result: 'received' }>('POST', '/public/helper-shifts/signup', input, publicRoute),
  cancelPreview: (token: string) =>
    apiRequest<{
      eventTitle: string;
      organizationName: string;
      shiftName: string;
      startsAt: string | null;
      endsAt: string | null;
      location: string | null;
      alreadyCancelled: boolean;
      policy: { cancelMode: CancelMode; offerAllowed: boolean };
      request: OwnShiftRequest | null;
    }>('POST', '/public/helper-shifts/cancel-preview', { token }, publicRoute),
  cancel: (token: string, reason: string | null) =>
    apiRequest<{ result: 'cancelled' | 'requested' }>(
      'POST',
      '/public/helper-shifts/cancel',
      reason === null ? { token } : { token, reason },
      publicRoute,
    ),
  /** Eigene Anfrage zurückziehen bzw. den Platz abgeben (per Abmelde-Link). */
  publicRequest: (token: string, action: 'offer' | 'withdraw') =>
    apiRequest<{ result: 'handed_over' | 'offered' | 'withdrawn' }>(
      'POST',
      '/public/helper-shifts/request',
      { token, action },
      publicRoute,
    ),
};

/** Artikel an den Stationen: nie ein Preis, nie Personendaten der Verkäufer (HLP-05). */
export interface StationItem {
  itemNo: number;
  title: string;
  size: string | null;
  category: string | null;
  sellerNumber: number | null;
  isHelperSeller: boolean;
  displayName: string | null;
  status: string;
  /** Kennung des Artikels, wenn ein Bild vorliegt (Welle 4g, nur bei der Prüfung): lädt die Vorschau. */
  imageItemId?: string | null;
  imageVersion?: number;
}

export type ReturnResult =
  | 'returned'
  | 'already_returned'
  | 'undone'
  | 'sold'
  | 'not_received'
  /** Korb-Modus (O4): gehört zu einem anderen Verkäufer, nichts wurde geändert. */
  | 'other_basket';

/** Korb in der Auswahl der Rückgabe-Station (O4): nur Nummer und Zähler, keine Namen und keine Preise. */
export interface ReturnBasketSummary {
  registrationId: string;
  sellerNumber: number;
  open: number;
  returned: number;
  /** Ware abgeholt (Zeitpunkt der Bestätigung), `null` = nicht bestätigt; fehlt in älteren Zwischenspeichern. */
  pickupConfirmedAt?: string | null;
}

export interface ReturnBasketItem {
  itemId: string;
  itemNo: number;
  code: string;
  externalCode: string | null;
  title: string;
  size: string | null;
  category: string | null;
  state: 'open' | 'returned';
  /** Als vermisst vermerkt (zählt als offen, wird vom Sammelbuchen nicht erfasst). Fehlt in älteren Zwischenspeichern. */
  missing?: boolean;
}

export interface ReturnBasket {
  registrationId: string;
  sellerNumber: number;
  /** Farbblock des Korbs (O5), `null` = keiner. */
  colorBlock?: ColorBlock | null;
  /** Ware abgeholt (Zeitpunkt der Bestätigung), `null` = nicht bestätigt; fehlt in älteren Zwischenspeichern. */
  pickupConfirmedAt?: string | null;
  items: ReturnBasketItem[];
}

/** Antwort von „Abgeholt bestätigen“ und Rücknahme; `openItems` = noch nicht zurückgegebene Artikel (Warnung, keine Sperre). */
export interface PickupState {
  registrationId: string;
  pickupConfirmedAt: string | null;
  openItems: number;
}

/** Helfer-Modus: Links, Helferliste, Beitritt und Stationen. */
export const helperApi = {
  createInvite: (
    eventId: string,
    input: {
      mode: 'approval' | 'auto';
      role: InviteRole;
      expiresInHours: number;
      /** Gültig bis zum Basarende (höchstens 30 Tage), `expiresInHours` gilt dann nicht. */
      untilEventEnd: boolean;
      maxUses: number | null;
    },
  ) =>
    apiRequest<HelperInvite & { token: string }>(
      'POST',
      `/events/${eventId}/helper-invites`,
      input,
    ),
  invites: (eventId: string) =>
    apiRequest<HelperInvite[]>('GET', `/events/${eventId}/helper-invites`),
  revokeInvite: (eventId: string, inviteId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/helper-invites/${inviteId}`),
  /** Den Link eines gültigen Einladungslinks erneut anzeigen (Token im Fragment). */
  inviteLink: (eventId: string, inviteId: string) =>
    apiRequest<{ url: string }>('GET', `/events/${eventId}/helper-invites/${inviteId}/link`),
  /** Aushang (A4) mit QR-Code zum Einladungslink; das Token steht nur im PDF. */
  invitePosterPdf: async (eventId: string, inviteId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/helper-invites/${inviteId}/poster.pdf`)).blob,
  /** Anwesenheit am Basartag für einen freigegebenen Team-Helfer. */
  setCheckIn: (eventId: string, requestId: string, present: boolean) =>
    apiRequest<undefined>(
      present ? 'POST' : 'DELETE',
      `/events/${eventId}/helpers/${requestId}/check-in`,
    ),
  helpers: (eventId: string) => apiRequest<HelperEntry[]>('GET', `/events/${eventId}/helpers`),
  /** Helferliste als Druckliste (Plan Nr. 22): schon vor dem Basar verfügbar, optional je Schicht (Nr. 33). */
  listPdf: async (eventId: string, shiftId?: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/helpers/list.pdf${shiftId === undefined ? '' : `?shiftId=${shiftId}`}`,
      )
    ).blob,
  /** Druckseite der Helferliste: dieselben Daten und Rechte wie das PDF, dazu der Briefkopf. */
  listDocument: (eventId: string, shiftId?: string) =>
    apiRequest<HelperListDocument>(
      'GET',
      `/events/${eventId}/helpers/list${shiftId === undefined ? '' : `?shiftId=${shiftId}`}`,
    ),
  /** `role` nur bei der Freigabe: nur gleich lassen oder auf „helper“ senken. */
  decide: (
    eventId: string,
    requestId: string,
    status: 'approved' | 'rejected' | 'removed',
    role?: InviteRole,
  ) =>
    apiRequest<undefined>(
      'PATCH',
      `/events/${eventId}/helpers/${requestId}`,
      role === undefined ? { status } : { status, role },
    ),
  /** Team-Helfer einer Schicht zuteilen; bei voller Schicht antwortet die API mit 409 `SHIFT_FULL`, `force` überbucht. */
  assignShift: (eventId: string, requestId: string, shiftId: string | null, force = false) =>
    apiRequest<undefined>('PATCH', `/events/${eventId}/helpers/${requestId}/shift`, {
      shiftId,
      force,
    }),
  preview: (token: string) =>
    apiRequest<{
      eventTitle: string;
      organizationName: string;
      startsAt: string | null;
      mode: 'approval' | 'auto';
      role: InviteRole;
      myStatus: HelperStatus | null;
      /** Gespeicherte Rolle der eigenen Anfrage, kann von `role` des Links abweichen. */
      myRole: InviteRole | null;
      inviteOk: boolean;
    }>('POST', '/helper-invites/preview', { token }),
  /** Vorschau ohne Anmeldung: nur Basar, Organisation, Termin und Rolle (ungültig: 404). */
  publicPreview: (token: string) =>
    apiRequest<{
      eventTitle: string;
      organizationName: string;
      startsAt: string | null;
      mode: 'approval' | 'auto';
      role: InviteRole;
    }>('POST', '/public/helper-invites/preview', { token }, publicRoute),
  join: (token: string) =>
    apiRequest<{ status: HelperStatus; eventId: string }>('POST', '/helper-invites/join', {
      token,
    }),
  myEvents: () => apiRequest<HelperEvent[]>('GET', '/me/helper-events'),
  returnScan: (
    eventId: string,
    code: string,
    undo = false,
    expectedRegistrationId?: string,
    guard?: { signal?: AbortSignal; stillValid?: () => boolean },
  ) =>
    apiRequest<{ result: ReturnResult; item: StationItem }>(
      'POST',
      `/events/${eventId}/returns/scan`,
      { code, undo, ...(expectedRegistrationId === undefined ? {} : { expectedRegistrationId }) },
      { ...SCAN_LIMITS, ...guard },
    ),
  /** Körbe der Rückgabe (O4), nur mit eingeschaltetem Korb-Modus (sonst 409 `BASKET_MODE_OFF`). */
  returnBaskets: (eventId: string) =>
    apiRequest<ReturnBasketSummary[]>('GET', `/events/${eventId}/returns/baskets`),
  returnBasket: (eventId: string, registrationId: string) =>
    apiRequest<ReturnBasket>('GET', `/events/${eventId}/returns/baskets/${registrationId}`),
  /** Rest des Korbs als zurückgegeben buchen (nur Warenannahme-Recht); `expectedCount` = „Gezählte Teile im Korb“, `batchId` = Vorgangs-ID (Wiederholung bucht nicht doppelt). */
  returnRest: (eventId: string, registrationId: string, expectedCount: number, batchId?: string) =>
    apiRequest<{ batchId: string; returned: number }>(
      'POST',
      `/events/${eventId}/returns/baskets/${registrationId}/return-rest`,
      batchId === undefined ? { expectedCount } : { expectedCount, batchId },
    ),
  /** Nimmt einen Sammelvorgang `returnRest` zurück. */
  undoReturnRest: (eventId: string, registrationId: string, batchId: string) =>
    apiRequest<{ undone: number }>(
      'POST',
      `/events/${eventId}/returns/baskets/${registrationId}/undo-rest`,
      { batchId },
    ),
  /** „Abgeholt“ bestätigen (nur Warenannahme-Recht, idempotent). */
  confirmPickup: (eventId: string, registrationId: string) =>
    apiRequest<PickupState>(
      'POST',
      `/events/${eventId}/returns/baskets/${registrationId}/pickup`,
      {},
    ),
  /** Nimmt „Abgeholt“ zurück. */
  undoPickup: (eventId: string, registrationId: string) =>
    apiRequest<PickupState>(
      'POST',
      `/events/${eventId}/returns/baskets/${registrationId}/pickup/undo`,
      {},
    ),
  inspect: (eventId: string, code: string) =>
    apiRequest<StationItem>(
      'GET',
      `/events/${eventId}/items/inspect?code=${encodeURIComponent(code)}`,
    ),
  /** Ob die Plattform gerade befristeten Support-Zugriff auf diesen Basar hat (Plan Nr. 27, Transparenz
   * für Inhaber/Verwalter, nie die handelnde Person). */
  supportAccessStatus: (eventId: string) =>
    apiRequest<SupportAccessStatus>('GET', `/events/${eventId}/support-access`),
};

/** Kasse und Sync (Vertrag: `@barso/shared`, pos-protocol). */
export const posApi = {
  /**
   * Kasse zur Übergabe freigeben (Schichtwechsel, bisherige Person): Alle Verkäufe bis `lastSeq` müssen übertragen sein. Alle
   * Sitzungen der Kasse enden; der Einmal-Code erlaubt nur die Übernahme an diesem Gerät.
   */
  releaseRegister: (eventId: string, registerId: string, lastSeq: number) =>
    apiRequest<{ handoverToken: string; handoverExpiresAt: string }>(
      'POST',
      `/events/${eventId}/registers/${registerId}/release`,
      { lastSeq },
    ),
  /**
   * Kasse übernehmen (Schichtwechsel, nächste Person): ausgewiesen mit dem Einmal-Code der Freigabe. Liefert die neue Sitzung;
   * die Kasse bleibt dieselbe.
   */
  handover: (eventId: string, registerId: string, handoverToken: string, lastSeq: number) =>
    apiRequest<{ sessionToken: string; sessionExpiresAt: string }>(
      'POST',
      `/events/${eventId}/registers/${registerId}/handover`,
      { handoverToken, lastSeq },
    ),
  snapshot: (
    eventId: string,
    query: { since?: string; afterId?: string; limit?: number },
    sessionToken?: string,
  ) => {
    const params = new URLSearchParams();
    if (query.since !== undefined) params.set('since', query.since);
    if (query.afterId !== undefined) params.set('afterId', query.afterId);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    const suffix = params.size === 0 ? '' : `?${params.toString()}`;
    return apiRequest<SnapshotResponse>('GET', `/events/${eventId}/snapshot${suffix}`, undefined, {
      ...sessionHeaders(sessionToken),
      ...SYNC_LIMITS,
    });
  },
  /** Neue Basar-Sitzung für die eigene Kasse (nach Abmeldung oder kurz vor dem Ablauf), nur mit Zugriffstoken. */
  renewSession: (eventId: string, registerId: string) =>
    apiRequest<{ sessionToken: string; sessionExpiresAt: string }>(
      'POST',
      `/events/${eventId}/registers/${registerId}/session`,
    ),
  sync: (eventId: string, registerId: string, body: SyncRequest, sessionToken?: string) =>
    apiRequest<SyncResponse>('POST', `/events/${eventId}/registers/${registerId}/sync`, body, {
      ...sessionHeaders(sessionToken),
      ...SYNC_LIMITS,
    }),
  /**
   * Sync NUR mit der Basar-Sitzung der Kasse (Hintergrund-Sender, U15a): ohne Zugriffstoken, ohne Erneuern und ohne Abmelden.
   * So spricht immer die Person, der die Kasse gehört, auch wenn am Gerät gerade jemand anderes angemeldet ist; eine
   * abgelehnte Sitzung (401) meldet niemanden ab.
   */
  syncWithSession: (eventId: string, registerId: string, body: SyncRequest, sessionToken: string) =>
    apiRequest<SyncResponse>('POST', `/events/${eventId}/registers/${registerId}/sync`, body, {
      auth: false,
      headers: { 'X-Register-Session': sessionToken },
      ...SYNC_LIMITS,
    }),
  /**
   * Kaufbeleg per E-Mail (Welle 5d): nur mit dem Konto der Kasse (Zugriffstoken), nie mit der Gerätesitzung. Die Adresse geht nur
   * in diesem Aufruf mit und liegt höchstens 30 Tage im Mail-Ausgang des Servers. `requestId` macht eine Wiederholung
   * derselben Anfrage unschädlich (keine zweite Mail innerhalb von 24 Stunden).
   */
  sendInvoice: (
    eventId: string,
    registerId: string,
    saleClientUuid: string,
    email: string,
    requestId: string,
  ) =>
    apiRequest<{ invoiceNo: number; totalCents: number; emailQueued: boolean; created: boolean }>(
      'POST',
      `/events/${eventId}/registers/${registerId}/sales/${saleClientUuid}/invoice`,
      { email, requestId },
    ),
  /**
   * Foto zu einem Artikel ohne Etikett (U17h): JPEG als Base64, idempotent über die Kennung der Kasse. Anmeldung wie beim
   * Sync: Zugriffstoken (mit Sitzung), nur die Basar-Sitzung (Hintergrund-Sender) oder das Geräte-Token (Schnellzugang).
   */
  uploadPhoto: (
    eventId: string,
    registerId: string,
    photoId: string,
    data: string,
    auth: { sessionToken?: string; sessionOnly?: boolean; deviceToken?: string },
  ) => {
    const path = `/events/${eventId}/registers/${registerId}/photos/${photoId}`;
    const limits = { timeoutMs: 30_000, idempotent: true } as const;
    if (auth.deviceToken !== undefined) {
      return asDevice(() =>
        apiRequest<{ id: string; bytes: number; created: boolean }>(
          'PUT',
          path,
          { data },
          deviceOptions(auth.deviceToken ?? '', limits),
        ),
      );
    }
    if (auth.sessionOnly === true && auth.sessionToken !== undefined) {
      return apiRequest<{ id: string; bytes: number; created: boolean }>(
        'PUT',
        path,
        { data },
        {
          auth: false,
          headers: { 'X-Register-Session': auth.sessionToken },
          ...limits,
        },
      );
    }
    return apiRequest<{ id: string; bytes: number; created: boolean }>(
      'PUT',
      path,
      { data },
      {
        ...sessionHeaders(auth.sessionToken),
        ...limits,
      },
    );
  },
  /** Sync einer Kasse mit Schnellzugang (Geräte-Token). 401 wird zu `DEVICE_REVOKED`. */
  syncDevice: (eventId: string, registerId: string, body: SyncRequest, deviceToken: string) =>
    asDevice(() =>
      apiRequest<SyncResponse>(
        'POST',
        `/events/${eventId}/registers/${registerId}/sync`,
        body,
        deviceOptions(deviceToken, SYNC_LIMITS),
      ),
    ),
  /** Artikelliste einer Kasse mit Schnellzugang (Geräte-Token). 401 wird zu `DEVICE_REVOKED`. */
  snapshotDevice: (
    eventId: string,
    query: { since?: string; afterId?: string; limit?: number },
    deviceToken: string,
  ) => {
    const params = new URLSearchParams();
    if (query.since !== undefined) params.set('since', query.since);
    if (query.afterId !== undefined) params.set('afterId', query.afterId);
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    const suffix = params.size === 0 ? '' : `?${params.toString()}`;
    return asDevice(() =>
      apiRequest<SnapshotResponse>(
        'GET',
        `/events/${eventId}/snapshot${suffix}`,
        undefined,
        deviceOptions(deviceToken, SYNC_LIMITS),
      ),
    );
  },
};

/** Einstellungen des Basar-Plakats (Verwaltung). */
export interface PosterSettings {
  layout: PosterLayout;
  headline: string | null;
  extraInfo: string | null;
  showDescription: boolean;
  showSchedule: boolean;
  showSellerInfo: boolean;
  qrMode: PosterQrMode;
  qrUrl: string | null;
  qrCaption: string | null;
  useCustom: boolean;
  hasLogo: boolean;
  custom: { contentType: string; filename: string; size: number } | null;
}

export type PosterSettingsInput = Omit<PosterSettings, 'hasLogo' | 'custom'>;

const POSTER_DOWNLOAD_TYPES = 'application/pdf, image/png, image/jpeg';

/** Basar-Plakat: Einstellungen, Logo, eigenes Plakat und Downloads. */
export const posterApi = {
  get: (eventId: string) => apiRequest<PosterSettings>('GET', `/events/${eventId}/poster`),
  save: (eventId: string, input: PosterSettingsInput) =>
    apiRequest<PosterSettings>('PUT', `/events/${eventId}/poster`, input),
  uploadLogo: (eventId: string, dataBase64: string) =>
    apiRequest<PosterSettings>(
      'PUT',
      `/events/${eventId}/poster/logo`,
      { dataBase64 },
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  removeLogo: (eventId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/poster/logo`),
  uploadCustom: (eventId: string, dataBase64: string, filename: string) =>
    apiRequest<PosterSettings>(
      'PUT',
      `/events/${eventId}/poster/custom`,
      { dataBase64, filename },
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  removeCustom: (eventId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/poster/custom`),
  /** Erzeugtes Plakat als Vorschau/Download (optional in einem anderen Layout als dem gespeicherten). */
  generatedPdf: async (eventId: string, layout?: PosterLayout): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/poster/pdf${layout === undefined ? '' : `?layout=${layout}`}`,
      )
    ).blob,
  customFile: async (eventId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/poster/custom`, undefined, POSTER_DOWNLOAD_TYPES))
      .blob,
  /** Öffentlicher Download für Besucher der Basar-Seite. */
  publicFile: async (orgSlug: string, eventSlug: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/public/events/${orgSlug}/${eventSlug}/poster`,
        undefined,
        POSTER_DOWNLOAD_TYPES,
      )
    ).blob,
  /** Logo des Basars (oder der Organisation) für die öffentliche Basar-Seite; ohne Logo 404. */
  publicLogo: async (orgSlug: string, eventSlug: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/public/events/${orgSlug}/${eventSlug}/logo`,
        undefined,
        'image/png, image/jpeg',
      )
    ).blob,
};

/** Digitale Unterschrift (Welle 3): Schalter je Basar, Stand und Aufnahme am Gerät des Teams. */
export type SignatureKind = 'terms' | 'payout';
export interface SignatureStatus {
  enabled: boolean;
  signedAt: string | null;
  /** Erklärung, die jetzt bestätigt würde (bei "payout" mit dem Betrag). */
  statement: string;
  /** Ausgewiesener Betrag (nur "payout" nach dem Abrechnen). */
  amountCents: number | null;
  /** Die jüngste Unterschrift gilt einem anderen Betrag: neu einholen. */
  stale: boolean;
  /** Grund, warum gerade nicht unterschrieben werden kann. */
  notReady: string | null;
}
const signaturePath = (eventId: string, registrationId: string, kind: SignatureKind): string =>
  kind === 'terms'
    ? `/events/${eventId}/intake/sellers/${registrationId}/signature`
    : `/events/${eventId}/settlement/sellers/${registrationId}/signature`;

export const signaturesApi = {
  settings: (eventId: string) =>
    apiRequest<{ enabled: boolean }>('GET', `/events/${eventId}/signatures/settings`),
  setEnabled: (eventId: string, enabled: boolean) =>
    apiRequest<{ enabled: boolean }>('PUT', `/events/${eventId}/signatures/settings`, { enabled }),
  status: (eventId: string, registrationId: string, kind: SignatureKind) =>
    apiRequest<SignatureStatus>('GET', signaturePath(eventId, registrationId, kind)),
  /** Nur der Schalter (einmal je Seite). */
  enabled: (eventId: string) =>
    apiRequest<{ enabled: boolean }>('GET', `/events/${eventId}/signatures/enabled`),
  capture: (
    eventId: string,
    registrationId: string,
    kind: SignatureKind,
    dataBase64: string,
    amountCents: number | null,
  ) =>
    apiRequest<SignatureStatus>('POST', signaturePath(eventId, registrationId, kind), {
      dataBase64,
      amountCents,
    }),
  /** Bild der jüngsten Unterschrift (nur Verwaltung mit Recht für Kontaktdaten). */
  image: async (eventId: string, registrationId: string, kind: SignatureKind): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/registrations/${registrationId}/signatures/${kind}/image`,
        undefined,
        'image/png',
      )
    ).blob,
};

/** Zahlen vor dem Versand einer Rundmail (keine Namen, keine Adressen). */
export interface RoundMailCounts {
  recipients: number;
  optedOut: number;
  mailedToday: number;
}
export interface ShiftCallPreview extends RoundMailCounts {
  openShifts: number;
  limitReached: boolean;
}

export const bazaarApi = {
  listEvents: (orgId: string) => apiRequest<BazaarEvent[]>('GET', `/orgs/${orgId}/events`),
  createEvent: (orgId: string, input: EventInput & { title: string }) =>
    apiRequest<BazaarEvent>('POST', `/orgs/${orgId}/events`, input),
  /** Basar anlegen aus Standardset, eigener Vorlage oder früherem Basar (U16b), alles in einer Anfrage. */
  createEventFrom: (orgId: string, input: CreateEventFromInput) =>
    apiRequest<BazaarEvent>('POST', `/orgs/${orgId}/events`, input, {
      timeoutMs: TIMEOUT_LONG_MS,
    }),
  /** Standardsets und eigene Vorlagen der Organisation, je mit Vorschau (U16b). */
  eventBlueprints: (orgId: string) =>
    apiRequest<BlueprintCatalog>('GET', `/orgs/${orgId}/event-blueprints`),
  /** „Als Vorlage speichern“ (U16b): Momentaufnahme eines Basars der Organisation. */
  createEventTemplate: (
    orgId: string,
    input: { name: string; description?: string | null; fromEventId: string },
  ) => apiRequest<EventTemplate>('POST', `/orgs/${orgId}/event-templates`, input),
  updateEventTemplate: (
    orgId: string,
    templateId: string,
    changes: { name?: string; description?: string | null },
  ) => apiRequest<EventTemplate>('PATCH', `/orgs/${orgId}/event-templates/${templateId}`, changes),
  /** Papierkorb: nach 30 Tagen endgültig gelöscht. */
  deleteEventTemplate: (orgId: string, templateId: string) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/event-templates/${templateId}`),
  getEvent: (eventId: string) =>
    apiRequest<BazaarEvent | EventSummary>('GET', `/events/${eventId}`),
  /** Kennzahlen und Zeilen des Basar-Hubs (U3a): Blöcke je Recht. */
  hub: (eventId: string) => apiRequest<HubSummary>('GET', `/events/${eventId}/hub`),
  updateEvent: (eventId: string, changes: EventInput) =>
    apiRequest<BazaarEvent>('PATCH', `/events/${eventId}`, changes),
  /** Basar wiederholen (X1): neuer Entwurf aus einem früheren Basar derselben Organisation. */
  copyEvent: (orgId: string, eventId: string, input: CopyEventInput) =>
    apiRequest<CopiedEvent>('POST', `/orgs/${orgId}/events/${eventId}/copy`, input, {
      timeoutMs: TIMEOUT_LONG_MS,
    }),
  /** Frühere Verkäufer (X1): nur Zahlen. */
  invitePreview: (orgId: string, eventId: string, sinceMonths: number) =>
    apiRequest<InvitePreview>(
      'GET',
      `/orgs/${orgId}/events/${eventId}/previous-sellers/preview?sinceMonths=${String(sinceMonths)}`,
    ),
  inviteSellers: (
    orgId: string,
    eventId: string,
    input: { sinceMonths: number; priority: boolean },
  ) =>
    apiRequest<{ invited: number }>(
      'POST',
      `/orgs/${orgId}/events/${eventId}/previous-sellers/invite`,
      input,
    ),
  inviteSchedule: (orgId: string, eventId: string) =>
    apiRequest<InviteSchedule | null>(
      'GET',
      `/orgs/${orgId}/events/${eventId}/previous-sellers/schedule`,
    ),
  planInvite: (
    orgId: string,
    eventId: string,
    input: { runAt: string; sinceMonths: number; sourceEventIds?: string[] },
  ) =>
    apiRequest<InviteSchedule>(
      'PUT',
      `/orgs/${orgId}/events/${eventId}/previous-sellers/schedule`,
      input,
    ),
  cancelInviteSchedule: (orgId: string, eventId: string) =>
    apiRequest<undefined>('DELETE', `/orgs/${orgId}/events/${eventId}/previous-sellers/schedule`),
  /** Nur die eigene Sicht: ab wann darf ich mich zu diesem Basar anmelden? */
  registrationWindow: (eventId: string) =>
    apiRequest<RegistrationWindow>('GET', `/me/events/${eventId}/registration-window`),
  /** Öffentlich: Einladungen abbestellen (Token aus dem Fragment der Seite, 204 immer). */
  optOutInvites: (token: string) =>
    apiRequest<undefined>('POST', '/public/invite-optout', { token }, publicRoute),
  inviteOptOuts: () => apiRequest<InviteOptOut[]>('GET', '/me/invite-optouts'),
  setInviteOptOut: (organizationId: string, optOut: boolean) =>
    apiRequest<InviteOptOut>('PUT', `/me/invite-optouts/${organizationId}`, { optOut }),
  /** Lager (X2). */
  carryoverCandidates: (registrationId: string) =>
    apiRequest<CarryoverGroup[]>('GET', `/me/registrations/${registrationId}/carryover`),
  carryOver: (registrationId: string, itemIds: string[]) =>
    apiRequest<CarryoverResult>('POST', `/me/registrations/${registrationId}/carryover`, {
      itemIds,
    }),
  transition: (eventId: string, to: EventStatus) =>
    apiRequest<BazaarEvent>('POST', `/events/${eventId}/transition`, { to }),
  changeTier: (
    orgId: string,
    eventId: string,
    tier: LicenseTier,
    promoCode?: string,
    waiverTextId?: string,
  ) =>
    apiRequest<BazaarEvent>('PATCH', `/orgs/${orgId}/events/${eventId}/license`, {
      tier,
      promoCode,
      waiverTextId,
    }),
  /** Aktuell gültige Fassung eines Rechtstexts (jede angemeldete Person darf lesen). */
  legalText: (kind: string) => apiRequest<LegalText>('GET', `/legal-texts/${kind}`),
  /** Basar-Bedingungen (Nr. 32): aktuelle Fassung, `null` = noch keine hinterlegt. */
  eventTerms: (eventId: string) =>
    apiRequest<{ id: string; content: string; createdAt: string } | null>(
      'GET',
      `/events/${eventId}/terms`,
    ),
  setEventTerms: (eventId: string, content: string) =>
    apiRequest<{ id: string; content: string; createdAt: string }>(
      'PUT',
      `/events/${eventId}/terms`,
      { content },
    ),
  /** Basar-Team (ORG-06): wer Kasse/Annahme/Verwaltung machen darf. Nur Organisationsmitglieder
   * können zugeteilt werden (sonst 404, kein Rückschluss auf fremde Konten). */
  team: (eventId: string) => apiRequest<TeamMember[]>('GET', `/events/${eventId}/team`),
  /** Auswahlliste für die Zuteilung: über event.team.manage erreichbar, nicht org.members.manage
   * (sonst könnte eine nur per Basar zugeteilte Managerin ihr eigenes Team nie erweitern). */
  teamCandidates: (eventId: string) =>
    apiRequest<TeamCandidate[]>('GET', `/events/${eventId}/team/candidates`),
  addTeamMember: (eventId: string, userId: string, role: TeamRole) =>
    apiRequest<undefined>('POST', `/events/${eventId}/team`, { userId, role }),
  /** Team-Matrix: alle Rollen einer Person auf einmal setzen, Antwort ist die neue Team-Liste. */
  setTeamRoles: (eventId: string, userId: string, roles: TeamRole[], expected: TeamRole[]) =>
    apiRequest<TeamMember[]>('PUT', `/events/${eventId}/team/${userId}`, { roles, expected }),
  removeTeamMember: (eventId: string, userId: string, role: TeamRole) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/team/${userId}/${role}`),
  /** Ablaufplan/Kalender: aktuelle Liste, geordnet. */
  schedule: (eventId: string) => apiRequest<ScheduleItem[]>('GET', `/events/${eventId}/schedule`),
  /** Ersetzt die ganze Liste (nicht anhängen). */
  setSchedule: (eventId: string, items: ScheduleItemInput[]) =>
    apiRequest<ScheduleItem[]>('PUT', `/events/${eventId}/schedule`, { items }),
  publicEvent: (orgSlug: string, eventSlug: string) =>
    apiRequest<PublicEvent>('GET', `/public/events/${orgSlug}/${eventSlug}`, undefined, {
      auth: false,
    }),
  /** Plattformweiter Basar-Kalender (EVT-11, „Basare entdecken“), öffentlich. */
  marketplace: () =>
    apiRequest<MarketplaceEntry[]>('GET', '/public/marketplace', undefined, { auth: false }),
  registerSeller: (
    orgSlug: string,
    eventSlug: string,
    labelName: string | null,
    termsTextId?: string,
  ) =>
    apiRequest<Registration>('POST', `/public/events/${orgSlug}/${eventSlug}/register`, {
      labelName,
      termsTextId,
      // Das Anmeldeformular zeigt den Hinweis auf spätere Einladungen (X1) immer an
      inviteNoticeSeen: true,
    }),
  myRegistrations: () => apiRequest<MyRegistration[]>('GET', '/me/registrations'),
  cancelRegistration: (id: string) =>
    apiRequest<Registration>('POST', `/me/registrations/${id}/cancel`),
  listItems: (registrationId: string) =>
    apiRequest<Item[]>('GET', `/me/registrations/${registrationId}/items`),
  /** Foto eines von der Leitung zugeordneten Artikels ohne Etikett (U17i), nur wenn freigegeben. */
  sellerSalePhoto: async (registrationId: string, photoId: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/me/registrations/${registrationId}/sale-photos/${photoId}`,
        undefined,
        'image/jpeg',
      )
    ).blob,
  createItem: (registrationId: string, input: ItemInput & { title: string; priceCents: number }) =>
    apiRequest<Item>('POST', `/me/registrations/${registrationId}/items`, input),
  updateItem: (registrationId: string, itemId: string, changes: ItemInput) =>
    apiRequest<Item>('PATCH', `/me/registrations/${registrationId}/items/${itemId}`, changes),
  /** Dupliziert einen Artikel (Nr. 29): neue Zeile mit denselben Angaben, eigenem Code. */
  duplicateItem: (registrationId: string, itemId: string) =>
    apiRequest<Item>('POST', `/me/registrations/${registrationId}/items/${itemId}/duplicate`),
  /** Kategorie-Vorschläge (Nr. 30): plattformweite Standards plus eigene Ergänzungen der Organisation. */
  categorySuggestions: (registrationId: string) =>
    apiRequest<string[]>('GET', `/me/registrations/${registrationId}/category-suggestions`),
  /** Massenimport (SEL-04): ganz oder gar nicht, Fehler kommen mit Zeilennummer (IMPORT_INVALID). */
  importItems: (
    registrationId: string,
    items: {
      row: number;
      title: string;
      size: string | null;
      category: string | null;
      priceCents: number;
      donate: boolean;
      externalCode?: string | null;
      discountAllowed?: boolean;
    }[],
  ) =>
    apiRequest<{ created: number }>(
      'POST',
      `/me/registrations/${registrationId}/items/import`,
      { items },
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  /**
   * Foto-Erkennung (C7): schickt das verkleinerte Foto (Base64, reines JPEG ohne EXIF) und bekommt einen Vorschlag.
   * Der Server speichert nichts; Barcodes liest nur der Browser.
   */
  recognizeItem: (registrationId: string, image: string) =>
    // Hochladen des Fotos plus Erkennung (Server-Zeitlimit 20 s): im Hallen-Netz deutlich mehr als die üblichen 30 s Puffer
    apiRequest<PhotoSuggestion>(
      'POST',
      `/me/registrations/${registrationId}/items/recognize`,
      { image },
      { timeoutMs: TIMEOUT_LONG_MS, idempotent: true },
    ),
  /**
   * KI-Artikelliste (Welle 4h): Text/CSV oder verkleinertes Foto einer Liste -> Entwürfe. Der Server legt nichts an;
   * jeder Entwurf wird vom Verkäufer geprüft und einzeln über `createItem` übernommen.
   */
  draftItemList: (registrationId: string, input: { text: string } | { image: string }) =>
    apiRequest<ListDraftResult>(
      'POST',
      `/me/registrations/${registrationId}/items/draft-list`,
      input,
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  /** Vorabprüfung vorhandener Etiketten (höchstens 50 je Aufruf); nur eine Hilfe, verbindlich entscheidet das Speichern. */
  checkExternalCodes: (registrationId: string, codes: string[]) =>
    apiRequest<{ code: string; status: ExternalCodeStatus }[]>(
      'POST',
      `/me/registrations/${registrationId}/external-codes/check`,
      { codes },
    ),
  /** Vorhandenes Etikett übernehmen (fremder Code) oder mit `null` entfernen. */
  setExternalCode: (registrationId: string, itemId: string, code: string | null) =>
    apiRequest<Item>('PUT', `/me/registrations/${registrationId}/items/${itemId}/external-code`, {
      code,
    }),
  /** Einverständnis mit der Rabattaktion für alle noch bearbeitbaren Artikel setzen oder entziehen (X7, „alle markieren“). */
  setAllDiscountAllowed: (registrationId: string, allowed: boolean) =>
    apiRequest<{ changed: number }>(
      'POST',
      `/me/registrations/${registrationId}/items/discount-allowed`,
      { allowed },
    ),
  deleteItem: (registrationId: string, itemId: string) =>
    apiRequest<undefined>('DELETE', `/me/registrations/${registrationId}/items/${itemId}`),
  /** Artikelbild (Welle 4g): Original als Base64, der Server prüft den Typ, verkleinert und speichert nur die kleine Fassung. */
  putItemImage: (registrationId: string, itemId: string, dataBase64: string) =>
    apiRequest<{ hasImage: true; width: number; height: number; bytes: number }>(
      'PUT',
      `/me/registrations/${registrationId}/items/${itemId}/image`,
      { dataBase64 },
      { timeoutMs: 120_000, idempotent: true },
    ),
  deleteItemImage: (registrationId: string, itemId: string) =>
    apiRequest<undefined>('DELETE', `/me/registrations/${registrationId}/items/${itemId}/image`),
  /** Das gespeicherte Bild des eigenen Artikels (verkleinerte JPEG-Fassung). */
  ownItemImage: async (registrationId: string, itemId: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/me/registrations/${registrationId}/items/${itemId}/image`,
        undefined,
        'image/jpeg',
      )
    ).blob,
  /** Das Bild eines Artikels für das Team (Annahme, Kasse, Prüfen); Recht `event.items.inspect`. */
  teamItemImage: async (eventId: string, itemId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/items/${itemId}/image`, undefined, 'image/jpeg'))
      .blob,
  /** Etiketten-PDF (ohne `itemIds`: alle noch nicht gedruckten Artikel). */
  labelPdf: async (
    registrationId: string,
    input: { grid: LabelGridId; skip: number; itemIds?: string[] },
  ): Promise<{ blob: Blob; renderedAt: string | null }> => {
    const { blob, headers } = await apiDownload(
      'POST',
      `/me/registrations/${registrationId}/labels/pdf`,
      input,
    );
    return { blob, renderedAt: headers.get('X-Rendered-At') };
  },
  /** `renderedAt`: Stand des PDF. Artikel, die sich seither änderten (z. B. neuer Preis), bleiben ungedruckt. */
  markPrinted: (registrationId: string, itemIds: string[], renderedAt: string) =>
    apiRequest<{ marked: number; changedSince: number }>(
      'POST',
      `/me/registrations/${registrationId}/labels/printed`,
      { itemIds, renderedAt },
    ),
  labelTestPdf: async (grid: LabelGridId): Promise<Blob> =>
    (await apiDownload('GET', `/labels/test.pdf?grid=${grid}`)).blob,
  /** Kisten-/Taschenetikett (Nr. 34): unabhängig von Artikeln, sobald die Anmeldung freigegeben ist. */
  /** Legende der Farbblöcke (O5) als Aushang; nur Verwaltende, ohne Regeln 409 `NO_COLOR_RULES`. */
  colorLegendPdf: async (eventId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/labels/color-legend.pdf`)).blob,
  boxTagPdf: async (registrationId: string): Promise<Blob> =>
    (await apiDownload('GET', `/me/registrations/${registrationId}/labels/box-tag.pdf`)).blob,
  intakeSellers: (eventId: string) =>
    apiRequest<IntakeSeller[]>('GET', `/events/${eventId}/intake/sellers`),
  intakeItems: (eventId: string, registrationId: string) =>
    apiRequest<IntakeItem[]>('GET', `/events/${eventId}/intake/sellers/${registrationId}/items`),
  /** Artikelsuche eines Basars (Plan Nr. 21); `signal` bricht veraltete Anfragen ab. */
  searchItems: (
    eventId: string,
    params: { q?: string; status?: string; sellerNumber?: number; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) query.set(key, String(value));
    const suffix = query.size === 0 ? '' : `?${query.toString()}`;
    return apiRequest<ItemSearchResult>(
      'GET',
      `/events/${eventId}/items${suffix}`,
      undefined,
      signal === undefined ? {} : { signal },
    );
  },
  intakeScan: (
    eventId: string,
    code: string,
    guard?: { signal?: AbortSignal; stillValid?: () => boolean },
  ) =>
    apiRequest<ReceiveResult>(
      'POST',
      `/events/${eventId}/intake/scan`,
      { code },
      { ...SCAN_LIMITS, ...guard },
    ),
  /** KI-Bildzuordnung (Welle 4k) in diesem Basar nutzbar: Plattform-Schalter, Basar-Schalter und Schlüssel (vom Server geprüft). */
  intakeAiMatchAvailable: (eventId: string) =>
    apiRequest<{ available: boolean }>('GET', `/events/${eventId}/intake/ai-match`),
  /**
   * KI-Bildzuordnung (Welle 4k): verkleinertes Foto eines Artikels ohne Etikett -> bis zu drei Vorschläge aus den offenen Artikeln
   * dieses Verkäufers. Der Server bucht nie; angenommen wird erst nach Bestätigung über `intakeSetReceived`.
   */
  intakeMatchPhoto: (eventId: string, registrationId: string, image: string) =>
    apiRequest<MatchResult>(
      'POST',
      `/events/${eventId}/intake/sellers/${registrationId}/match-photo`,
      { image },
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  intakeSetReceived: (eventId: string, itemId: string, received: boolean) =>
    apiRequest<ReceiveResult>('PATCH', `/events/${eventId}/intake/items/${itemId}`, {
      received,
    }),
  /** Kistenzahl, Haken „eingecheckt“ und Bestätigung der Bedingungen eines Verkäufers (nur im Status „Annahme“). */
  intakeUpdateSeller: (
    eventId: string,
    registrationId: string,
    changes: { crateCount?: number | null; checkedIn?: boolean; confirmTerms?: true },
  ) =>
    apiRequest<IntakeSeller>(
      'PATCH',
      `/events/${eventId}/intake/sellers/${registrationId}`,
      changes,
    ),
  intakeReceiveAll: (eventId: string, registrationId: string) =>
    apiRequest<{ received: number; seller: IntakeSeller }>(
      'POST',
      `/events/${eventId}/intake/sellers/${registrationId}/receive-all`,
    ),
  /** Rückgabeliste als PDF: für einen Verkäufer oder (ohne `registrationId`) für alle mit offenen Artikeln. */
  returnListPdf: async (eventId: string, registrationId?: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        registrationId === undefined
          ? `/events/${eventId}/returns/list.pdf`
          : `/events/${eventId}/returns/sellers/${registrationId}/list.pdf`,
      )
    ).blob,
  /** Druckseite der Rückgabeliste (alle Verkäufer oder einer): dieselben Daten und Rechte wie das PDF, dazu der Briefkopf. */
  returnListDocument: (eventId: string, registrationId?: string) =>
    apiRequest<ReturnListDocument>(
      'GET',
      registrationId === undefined
        ? `/events/${eventId}/returns/list`
        : `/events/${eventId}/returns/sellers/${registrationId}/list`,
      undefined,
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  /** Bedingungsblatt (Druckseite) für eine freigegebene Anmeldung. */
  termsSheetDocument: (eventId: string, registrationId: string) =>
    apiRequest<TermsSheetDocument>(
      'GET',
      `/events/${eventId}/intake/sellers/${registrationId}/terms-sheet`,
      undefined,
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  /** Bedingungsblatt als PDF (A4) für eine Anmeldung. */
  termsSheetPdf: async (eventId: string, registrationId: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        `/events/${eventId}/intake/sellers/${registrationId}/terms-sheet.pdf`,
      )
    ).blob,
  /** Verkaufsliste eines Verkäufers (Druckseite). */
  salesListDocument: (eventId: string, registrationId: string) =>
    apiRequest<SalesListDocument>(
      'GET',
      `/events/${eventId}/sellers/${registrationId}/sales-list`,
      undefined,
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  /** Verkaufsliste als PDF (zweispaltig). */
  salesListPdf: async (eventId: string, registrationId: string): Promise<Blob> =>
    (await apiDownload('GET', `/events/${eventId}/sellers/${registrationId}/sales-list.pdf`)).blob,
  /** Mängelzettel: leer oder mit der Verkäufernummer einer freigegebenen Anmeldung (Druckseite). */
  defectNoticeDocument: (eventId: string, registrationId?: string) =>
    apiRequest<DefectNoticeDocument>(
      'GET',
      registrationId === undefined
        ? `/events/${eventId}/intake/defect-notice`
        : `/events/${eventId}/intake/sellers/${registrationId}/defect-notice`,
      undefined,
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  /** Mängelzettel als PDF (A5 quer). */
  defectNoticePdf: async (eventId: string, registrationId?: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        registrationId === undefined
          ? `/events/${eventId}/intake/defect-notice.pdf`
          : `/events/${eventId}/intake/sellers/${registrationId}/defect-notice.pdf`,
      )
    ).blob,
  /** Abholscheine (vier je Blatt): alle freigegebenen Verkäufer oder (mit `registrationId`) einer. */
  pickupSlipDocument: (eventId: string, registrationId?: string) =>
    apiRequest<PickupSlipDocument>(
      'GET',
      registrationId === undefined
        ? `/events/${eventId}/returns/pickup-slips`
        : `/events/${eventId}/returns/sellers/${registrationId}/pickup-slip`,
      undefined,
      { timeoutMs: TIMEOUT_LONG_MS },
    ),
  /** Abholschein als PDF (alle oder einer). */
  pickupSlipPdf: async (eventId: string, registrationId?: string): Promise<Blob> =>
    (
      await apiDownload(
        'GET',
        registrationId === undefined
          ? `/events/${eventId}/returns/pickup-slips.pdf`
          : `/events/${eventId}/returns/sellers/${registrationId}/pickup-slip.pdf`,
      )
    ).blob,
  /** Druckseite der Verkäuferliste (freigegebene Verkäufer mit Name und Artikelzahl). */
  sellerPrintList: (eventId: string) =>
    apiRequest<SellerPrintList>('GET', `/events/${eventId}/sellers/list`),
  listSellers: (eventId: string) => apiRequest<SellerEntry[]>('GET', `/events/${eventId}/sellers`),
  /** Lückenliste der Verkäufernummern mit Notiz und „war x-mal dabei“ (Welle 1, Punkt 5). */
  sellerGaps: (eventId: string) => apiRequest<SellerGaps>('GET', `/events/${eventId}/seller-gaps`),
  setGapNote: (eventId: string, sellerNumber: number, note: string | null) =>
    apiRequest<undefined>('PUT', `/events/${eventId}/seller-gaps/${String(sellerNumber)}/note`, {
      note,
    }),
  /** Interne Notizen zu Personen des Basars (nur Verwaltende). */
  personNotes: (eventId: string) =>
    apiRequest<{ registrationId: string; note: string }[]>(
      'GET',
      `/events/${eventId}/seller-notes`,
    ),
  setPersonNote: (eventId: string, registrationId: string, note: string | null) =>
    apiRequest<undefined>('PUT', `/events/${eventId}/sellers/${registrationId}/note`, { note }),
  /** Auswertung „lange nicht angeboten“ (Namen und Adressen, Recht zu Kontaktdaten). */
  notOffered: (eventId: string, sinceMonths: number) =>
    apiRequest<NotOfferedEntry[]>(
      'GET',
      `/events/${eventId}/not-offered?sinceMonths=${String(sinceMonths)}`,
    ),
  /** Person vom Inaktivitätslauf ausnehmen oder wieder einbeziehen (Welle 1, Punkt 6). */
  setSellerInactivityExempt: (eventId: string, registrationId: string, exempt: boolean) =>
    apiRequest<undefined>('PATCH', `/events/${eventId}/sellers/${registrationId}/inactivity`, {
      exempt,
    }),
  /** Anmeldeart setzen: normal, mit Helferschicht, Veranstalter-Verkäufer (Welle 2b, reine Kennzeichnung). */
  /** Verkäufer-Gruppen für einen Basar: Liste mit Vermerk „aktiviert“ und Aktivierung (Welle 2b). */
  listEventSellerGroups: (eventId: string) =>
    apiRequest<(SellerGroup & { activated: boolean })[]>('GET', `/events/${eventId}/seller-groups`),
  activateSellerGroup: (eventId: string, groupId: string) =>
    apiRequest<SellerGroupActivation>(
      'POST',
      `/events/${eventId}/seller-groups/${groupId}/activate`,
    ),
  setSellerRegistrationKind: (eventId: string, registrationId: string, kind: RegistrationKind) =>
    apiRequest<undefined>('PATCH', `/events/${eventId}/sellers/${registrationId}/kind`, { kind }),
  /** VIP-Reservierung der Verkäufernummern (Welle 1, Punkt 4). */
  listReservedNumbers: (eventId: string) =>
    apiRequest<ReservedNumber[]>('GET', `/events/${eventId}/reserved-numbers`),
  reserveNumber: (
    eventId: string,
    body: {
      sellerNumber: number;
      registrationId?: string | null;
      reservedUntil?: string | null;
      note?: string | null;
    },
  ) => apiRequest<ReservedNumber>('POST', `/events/${eventId}/reserved-numbers`, body),
  releaseReservedNumber: (eventId: string, reservationId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/reserved-numbers/${reservationId}`),
  /** „Warteliste anschreiben“ (Welle 4j): Vorschau nur mit Zahlen, dann Versand. */
  waitlistMailPreview: (eventId: string) =>
    apiRequest<RoundMailCounts>('GET', `/events/${eventId}/waitlist-mail/preview`),
  sendWaitlistMail: (eventId: string) =>
    apiRequest<{ sent: number }>('POST', `/events/${eventId}/waitlist-mail`),
  decideSeller: (
    eventId: string,
    registrationId: string,
    decision: { status: 'approved' | 'waitlist' | 'rejected'; isHelper?: boolean },
  ) => apiRequest<SellerEntry>('PATCH', `/events/${eventId}/sellers/${registrationId}`, decision),
  /** Bonusstufen des Basars (W3-Bonus-a): 404, wenn der Helferbonus ausgeschaltet ist. */
  bonusTiers: (eventId: string) =>
    apiRequest<BonusTiersView>('GET', `/events/${eventId}/bonus-tiers`),
  saveBonusTiers: (
    eventId: string,
    tiers: BonusTierInput[],
    commissionBasis?: CommissionShiftBasis,
    entryRequiresUnlock?: boolean,
    publicShown?: boolean,
  ) =>
    apiRequest<BonusTiersView>('PUT', `/events/${eventId}/bonus-tiers`, {
      tiers,
      ...(commissionBasis === undefined ? {} : { commissionBasis }),
      ...(entryRequiresUnlock === undefined ? {} : { entryRequiresUnlock }),
      ...(publicShown === undefined ? {} : { publicShown }),
    }),
  /** „Nur zuweisen“-Stufe (z. B. Orga-Team) setzen; `null` hebt die Zuweisung auf. */
  assignBonusTier: (eventId: string, registrationId: string, tierId: string | null) =>
    apiRequest<{ registrationId: string; tierId: string | null }>(
      'PUT',
      `/events/${eventId}/sellers/${registrationId}/bonus-tier`,
      { tierId },
    ),
  /** Manuelle Ausnahme (Plan Nr. 24): auch über die Höchstzahl hinaus, per E-Mail-Adresse. */
  addSellerManually: (eventId: string, email: string) =>
    apiRequest<SellerEntry>('POST', `/events/${eventId}/sellers/manual`, { email }),
  /** Vorschläge für „manuell hinzufügen“ (C4): frühere Verkäufer der Organisation, nur für Inhaber/Verwalter. */
  searchSellerContacts: (eventId: string, q: string, signal?: AbortSignal) =>
    apiRequest<{ firstName: string; lastName: string; email: string }[]>(
      'GET',
      `/events/${eventId}/contacts?q=${encodeURIComponent(q)}`,
      undefined,
      signal === undefined ? {} : { signal },
    ),
};

// ---------------------------------------------------------------- Nachrichten und Neuigkeiten (Plan Nr. 40)

export type { AudienceGroup };

export type ThreadKind = 'direct' | 'broadcast';
export type MessageSide = 'team' | 'participant';

/** Eine Nachricht im Verlauf: nur Text (nie HTML), geschwärzte Nachrichten haben `redacted` und leeren Text. */
export interface MessageItem {
  id: string;
  side: MessageSide;
  mine: boolean;
  senderName: string;
  text: string;
  redacted: boolean;
  createdAt: string;
}

export interface MessageCursor {
  beforeAt: string;
  beforeId: string;
}

/** Faden aus Sicht einer Person (Verkäufer, Bewerber, Helfer, Annahme, Kasse). */
export interface MessageThread {
  id: string;
  scope: 'event';
  eventId: string;
  eventTitle: string;
  organizationId: string;
  organizationName: string;
  kind: ThreadKind;
  subject: string;
  /** Kennzeichnung eines Rundschreibens („an Kassen-Team“), nie Anzahl oder Empfänger. Gespräche: `null`. */
  audienceLabel: string | null;
  lastMessageAt: string;
  unread: boolean;
  canReply: boolean;
}

/** Rundschreiben der Organisation in der Liste des Posteingangs (Plan Nr. 11). */
export interface OrgMessageEntry {
  id: string;
  scope: 'org';
  eventId: null;
  eventTitle: null;
  organizationId: string;
  organizationName: string;
  kind: 'broadcast';
  subject: string;
  audienceLabel: string | null;
  lastMessageAt: string;
  unread: boolean;
  canReply: false;
}

export type InboxEntry = MessageThread | OrgMessageEntry;

/** Abschnitt des Posteingangs: ein Basar oder eine Organisation (keine Ordner). */
export interface InboxSection {
  type: 'event' | 'org';
  id: string;
  title: string;
  organizationName: string;
  unread: number;
  lastMessageAt: string;
}

/** Ein Rundschreiben der Organisation aus Sicht einer Empfängerin. */
export interface OrgBroadcastView {
  id: string;
  organizationId: string;
  organizationName: string;
  subject: string;
  audienceLabel: string;
  createdAt: string;
  unread: boolean;
  senderName: string;
  text: string;
  redacted: boolean;
}

/** Ein Rundschreiben der Organisation aus Sicht des Teams (mit Zahlen). */
export interface OrgBroadcastSummary {
  id: string;
  subject: string;
  audiences: ('org_admins' | 'org_members')[];
  audienceLabel: string;
  recipientCount: number;
  readCount: number;
  createdAt: string;
  redacted: boolean;
}

export interface MessageThreadDetail extends MessageThread {
  messages: MessageItem[];
  hasMore: boolean;
}

export interface MessageContact {
  eventId: string;
  eventTitle: string;
  organizationName: string;
  groups: AudienceGroup[];
}

/** Person auf der Gegenseite des Teams; der Nachname nur mit dem Recht für Kontaktdaten. */
export interface MessageParticipant {
  userId: string;
  firstName: string;
  lastName: string | null;
  sellerNumber: number | null;
}

export interface MessageRecipient extends MessageParticipant {
  groups: AudienceGroup[];
}

/** Faden aus Sicht des Teams (Gespräch oder Rundschreiben). */
export interface TeamThread {
  id: string;
  kind: ThreadKind;
  subject: string;
  participant: MessageParticipant | null;
  audiences: AudienceGroup[];
  /** Beim Senden festgeschriebene Kennzeichnung („an Kassen-Team, Schicht „X““), nur bei Rundschreiben. */
  audienceLabel: string | null;
  recipientCount: number;
  /** Nur bei Rundschreiben: wie vielen Externen der Inhalt per E-Mail geschickt wurde. */
  externalCount: number;
  /** Nur bei Rundschreiben: wie viele es schon geöffnet haben. */
  readCount: number | null;
  lastMessageAt: string;
  lastSenderSide: MessageSide;
  unread: boolean;
  createdAt: string;
}

export interface TeamThreadDetail extends TeamThread {
  messages: MessageItem[];
  hasMore: boolean;
}

/** Schicht als Ziel eines Rundschreibens (Plan Nr. 11). */
export interface BroadcastShiftSelection {
  shiftId: string;
  /** Wartende einschließen, Standard aus. */
  includeWaitlist: boolean;
  /** Externe ohne Konto per E-Mail einschließen. */
  includeExternal: boolean;
}

export interface BroadcastTarget {
  audiences: AudienceGroup[];
  userIds: string[];
  shifts: BroadcastShiftSelection[];
}

/** Vorschau eines Rundschreibens: nur Zahlen, nie Namen. */
export interface BroadcastPreview {
  /** Alle Personen (mit Konto und Externe). */
  recipients: number;
  /** Im Posteingang. */
  accounts: number;
  /** Nur per E-Mail (ohne Konto, Adresse bestätigt). */
  externals: number;
  /** Ohne Konto und ohne bestätigte Adresse: bekommen nichts (Zahl der Adressen). */
  unconfirmedExternals: number;
  groups: { key: string; label: string; count: number }[];
}

function pageQuery(
  cursor: MessageCursor | null,
  limit: number,
  extra: Record<string, string> = {},
): string {
  const query = new URLSearchParams({ limit: String(limit), ...extra });
  if (cursor !== null) {
    query.set('beforeAt', cursor.beforeAt);
    query.set('beforeId', cursor.beforeId);
  }
  return query.toString();
}

/** Eintrag des vereinten Postfachs (`GET /me/messages/mailbox`, Plan 16, 4.13). */
export interface MailboxEntry {
  id: string;
  /** `team`: Faden eines Basars mit Team-Recht, `mine`: eigener Faden, `org`: Rundschreiben der Organisation. */
  box: 'team' | 'mine' | 'org';
  eventId: string | null;
  eventTitle: string | null;
  organizationId: string;
  organizationName: string;
  kind: 'direct' | 'broadcast';
  subject: string;
  /** Person (Nachname nur mit Recht für Kontaktdaten), „An: …“ bei gesendeten Rundschreiben, „Team ‹Basar›“. */
  counterpart: string;
  /** Höchstens 120 Zeichen der letzten Nachricht, `null` bei geschwärzter Nachricht. */
  snippet: string | null;
  lastMessageAt: string;
  /** Gesendete Rundschreiben sind nie ungelesen. */
  unread: boolean;
  canReply: boolean;
  /** Nur gesendete Rundschreiben des Teams. */
  readCount: number | null;
  recipientCount: number | null;
}

export interface MailboxQuery {
  filter?: 'all' | 'unread';
  eventId?: string;
  orgId?: string;
  kind?: 'direct' | 'broadcast' | 'org';
}

export interface MailboxTeamBox {
  eventId: string;
  eventTitle: string;
  organizationId: string;
  organizationName: string;
  eventStatus: string;
  canBroadcast: boolean;
  /** Gespräche, auf die eine Antwort wartet. */
  unread: number;
}

export interface MailboxBoxes {
  /** Basare mit Team-Postfach (`event.messages.manage`). */
  team: MailboxTeamBox[];
  /** Basare, in denen die Person nur dem Team schreiben darf (ohne die eigenen Team-Basare). */
  mine: MessageContact[];
  /** Organisationen mit Recht für Rundschreiben. */
  orgs: { organizationId: string; organizationName: string }[];
}

/** Nachrichten: Personen-Seite unter `/me/messages`, Team-Seite unter `/events/:eventId/messages`. */
export const messagingApi = {
  /** Mit `eventId` oder `orgId` nur ein Abschnitt des Posteingangs, ohne Filter alle Basare. */
  myThreads: (
    cursor: MessageCursor | null = null,
    limit = 30,
    filter: { eventId?: string; orgId?: string } = {},
  ) =>
    apiRequest<{ threads: InboxEntry[]; next: MessageCursor | null }>(
      'GET',
      `/me/messages/threads?${pageQuery(cursor, limit, filter)}`,
    ),
  mySections: () => apiRequest<InboxSection[]>('GET', '/me/messages/sections'),
  myOrgBroadcast: (broadcastId: string) =>
    apiRequest<OrgBroadcastView>('GET', `/me/messages/org/${broadcastId}`),
  markOrgBroadcastRead: (broadcastId: string) =>
    apiRequest<undefined>('POST', `/me/messages/org/${broadcastId}/read`),
  /** Öffentlich: Nachrichten zu Schichten abbestellen (Token aus dem Fragment der Seite, 204 immer). */
  muteShiftMessages: (token: string) =>
    apiRequest<undefined>('POST', '/public/shift-signups/mute', { token }, publicRoute),
  /** Öffentlich: Adresse einer Eintragung ohne Konto bestätigen (Token aus dem Fragment der Seite, 204 immer). */
  confirmShiftEmail: (token: string) =>
    apiRequest<undefined>('POST', '/public/shift-signups/confirm', { token }, publicRoute),
  /** `threads`: eigene Fäden und Rundschreiben der Organisation, `team`: Team-Gespräche mit wartender Antwort. */
  myUnread: () => apiRequest<{ threads: number; team: number }>('GET', '/me/messages/unread'),
  /** Vereintes Postfach: eine Seite aus Team-Fäden, eigenen Fäden und Rundschreiben (fremder Filter ist 404). */
  mailbox: (cursor: MessageCursor | null = null, limit = 30, filter: MailboxQuery = {}) => {
    const extra: Record<string, string> = {};
    if (filter.filter !== undefined) extra['filter'] = filter.filter;
    if (filter.eventId !== undefined) extra['eventId'] = filter.eventId;
    if (filter.orgId !== undefined) extra['orgId'] = filter.orgId;
    if (filter.kind !== undefined) extra['kind'] = filter.kind;
    return apiRequest<{ entries: MailboxEntry[]; next: MessageCursor | null }>(
      'GET',
      `/me/messages/mailbox?${pageQuery(cursor, limit, extra)}`,
    );
  },
  mailboxBoxes: () => apiRequest<MailboxBoxes>('GET', '/me/messages/mailbox/boxes'),
  myContacts: () => apiRequest<MessageContact[]>('GET', '/me/messages/contacts'),
  startThread: (input: { eventId: string; subject: string; text: string }) =>
    apiRequest<MessageThreadDetail>('POST', '/me/messages/threads', input),
  myThread: (threadId: string, beforeId?: string) =>
    apiRequest<MessageThreadDetail>(
      'GET',
      `/me/messages/threads/${threadId}${beforeId === undefined ? '' : `?beforeId=${beforeId}`}`,
    ),
  replyMine: (threadId: string, text: string) =>
    apiRequest<MessageItem>('POST', `/me/messages/threads/${threadId}/messages`, { text }),
  markMineRead: (threadId: string) =>
    apiRequest<undefined>('POST', `/me/messages/threads/${threadId}/read`),
  teamThreads: (
    eventId: string,
    kind: ThreadKind | null = null,
    cursor: MessageCursor | null = null,
    limit = 30,
  ) =>
    apiRequest<{ threads: TeamThread[]; next: MessageCursor | null }>(
      'GET',
      `/events/${eventId}/messages/threads?${pageQuery(cursor, limit, kind === null ? {} : { kind })}`,
    ),
  teamUnread: (eventId: string) =>
    apiRequest<{ threads: number }>('GET', `/events/${eventId}/messages/unread`),
  recipients: (eventId: string) =>
    apiRequest<MessageRecipient[]>('GET', `/events/${eventId}/messages/recipients`),
  startTeamThread: (
    eventId: string,
    input: { recipientUserId: string; subject: string; text: string },
  ) => apiRequest<TeamThreadDetail>('POST', `/events/${eventId}/messages/threads`, input),
  teamThread: (eventId: string, threadId: string, beforeId?: string) =>
    apiRequest<TeamThreadDetail>(
      'GET',
      `/events/${eventId}/messages/threads/${threadId}${beforeId === undefined ? '' : `?beforeId=${beforeId}`}`,
    ),
  replyTeam: (eventId: string, threadId: string, text: string) =>
    apiRequest<MessageItem>('POST', `/events/${eventId}/messages/threads/${threadId}/messages`, {
      text,
    }),
  markTeamRead: (eventId: string, threadId: string) =>
    apiRequest<undefined>('POST', `/events/${eventId}/messages/threads/${threadId}/read`),
  previewBroadcast: (eventId: string, target: BroadcastTarget) =>
    apiRequest<BroadcastPreview>('POST', `/events/${eventId}/messages/broadcasts/preview`, target),
  sendBroadcast: (eventId: string, input: BroadcastTarget & { subject: string; text: string }) =>
    apiRequest<{ threadId: string; recipients: number; externals: number }>(
      'POST',
      `/events/${eventId}/messages/broadcasts`,
      input,
    ),
};

/** Rundschreiben der Organisation (Plan Nr. 11): nur Inhaber und Verwalter (`org.messages.broadcast`). */
export const orgMessagingApi = {
  preview: (orgId: string, audiences: ('org_admins' | 'org_members')[]) =>
    apiRequest<{ recipients: number; groups: { key: string; label: string; count: number }[] }>(
      'POST',
      `/orgs/${orgId}/messages/broadcasts/preview`,
      { audiences },
    ),
  send: (
    orgId: string,
    input: { audiences: ('org_admins' | 'org_members')[]; subject: string; text: string },
  ) =>
    apiRequest<{ broadcastId: string; recipients: number }>(
      'POST',
      `/orgs/${orgId}/messages/broadcasts`,
      input,
    ),
  list: (orgId: string, cursor: MessageCursor | null = null, limit = 30) =>
    apiRequest<{ broadcasts: OrgBroadcastSummary[]; next: MessageCursor | null }>(
      'GET',
      `/orgs/${orgId}/messages/broadcasts?${pageQuery(cursor, limit)}`,
    ),
  get: (orgId: string, broadcastId: string) =>
    apiRequest<OrgBroadcastSummary & { text: string }>(
      'GET',
      `/orgs/${orgId}/messages/broadcasts/${broadcastId}`,
    ),
};

/** Beitrag aus Sicht des Teams (Verwaltung). */
export interface NewsAdminPost {
  id: string;
  title: string;
  text: string;
  audiences: AudienceGroup[];
  pinned: boolean;
  createdAt: string;
  createdByName: string;
  updatedAt: string | null;
}

/** Beitrag aus Sicht einer Person. */
export interface NewsPost {
  id: string;
  eventId: string;
  eventTitle: string;
  organizationName: string;
  title: string;
  text: string;
  pinned: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface NewsInput {
  title: string;
  text: string;
  audiences: AudienceGroup[];
  pinned: boolean;
}

/** Neuigkeiten je Basar: Verwaltung durch das Team, Lesen unter `/me/news`. */
export const newsApi = {
  adminList: (eventId: string) => apiRequest<NewsAdminPost[]>('GET', `/events/${eventId}/news`),
  create: (eventId: string, input: NewsInput & { notify: boolean }) =>
    apiRequest<NewsAdminPost>('POST', `/events/${eventId}/news`, input),
  update: (eventId: string, postId: string, input: Partial<NewsInput>) =>
    apiRequest<NewsAdminPost>('PATCH', `/events/${eventId}/news/${postId}`, input),
  remove: (eventId: string, postId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/news/${postId}`),
  myNews: (eventId?: string, cursor: MessageCursor | null = null, limit = 50) =>
    apiRequest<{ posts: NewsPost[]; next: MessageCursor | null }>(
      'GET',
      `/me/news?${pageQuery(cursor, limit, eventId === undefined ? {} : { eventId })}`,
    ),
};

export interface NotificationSettings {
  emailMessages: boolean;
  emailNews: boolean;
  /** Erinnerungen aus dem Ablaufplan und an gebuchte Zeitfenster (X3, X8). */
  emailReminders: boolean;
  /** Mitteilung „N Artikel verkauft“ auf dem Gerät (gilt nur mit eingeschaltetem Gerät). */
  pushSales: boolean;
  /** Mitteilung „Ihre Kiste ist fertig zur Abholung“. */
  pushPickup: boolean;
}

/** Ein Dauer-Etikett der eigenen Person (Zeiten als ISO-Text, `null` = noch nicht eingetreten). */
export interface PoolLabelCode {
  id: string;
  /** Klartext aus 12 Ziffern. */
  code: string;
  createdAt: string;
  printedAt: string | null;
  revokedAt: string | null;
  /** Zuletzt einem Artikel zugeordnet (aus irgendeinem Basar). */
  lastUsedAt: string | null;
  assigned: boolean;
}

export interface PoolLabelList {
  codes: PoolLabelCode[];
  activeCount: number;
  activeLimit: number;
  /** Erlaubte Bogengrößen beim Erzeugen (12, 24, 48). */
  sheetSizes: number[];
}

/**
 * Dauer-Etiketten je Person, in allen Basaren nutzbar. Ist der Plattform-Hauptschalter aus, antwortet jede Route mit
 * 404: Die Oberfläche behandelt das als „nicht freigeschaltet“ (`isPoolLabelsUnavailable`).
 */
export const poolLabelApi = {
  list: () => apiRequest<PoolLabelList>('GET', '/me/label-codes'),
  create: (count: 12 | 24 | 48) =>
    apiRequest<{ codes: PoolLabelCode[] }>('POST', '/me/label-codes', { count }),
  /** PDF-Bogen. Ohne `codeIds`: alle noch nicht gedruckten, nicht gesperrten Codes. `name`: leer oder `null` = kein Name. */
  pdf: async (input: {
    grid: LabelGridId;
    skip: number;
    codeIds?: string[];
    name?: string | null;
  }): Promise<{ blob: Blob; renderedAt: string | null }> => {
    const { blob, headers } = await apiDownload('POST', '/me/label-codes/pdf', input);
    return { blob, renderedAt: headers.get('X-Rendered-At') };
  },
  markPrinted: (codeIds: string[]) =>
    apiRequest<{ marked: number }>('POST', '/me/label-codes/printed', { codeIds }),
  revoke: (codeId: string) => apiRequest<PoolLabelCode>('POST', `/me/label-codes/${codeId}/revoke`),
};

/** Der Server antwortet bei ausgeschaltetem Hauptschalter überall mit 404. */
export function isPoolLabelsUnavailable(error: unknown): boolean {
  return error instanceof ApiClientError && error.status === 404;
}

/** E-Mail-Hinweise bei neuer Nachricht oder Neuigkeit (nur die eigenen Einstellungen). */
export const notificationApi = {
  get: () => apiRequest<NotificationSettings>('GET', '/me/notification-settings'),
  /** Fehlende Felder lässt der Server unverändert (die E-Mail-Einstellungen und die Mitteilungen speichern getrennt). */
  put: (
    input: Pick<NotificationSettings, 'emailMessages' | 'emailNews'> &
      Partial<NotificationSettings>,
  ) => apiRequest<NotificationSettings>('PUT', '/me/notification-settings', input),
};

/** Ein Gerät, das Mitteilungen empfängt (ohne Geräte-Adresse). */
export interface PushDevice {
  id: string;
  kind: 'web' | 'apns' | 'fcm';
  deviceLabel: string | null;
  createdAt: string;
  lastSuccessAt: string | null;
}

export interface PushConfig {
  web: { available: boolean; vapidPublicKey: string | null };
  /** Der Abschnitt gehört nur dann in die Oberfläche (oder die Person hat schon Geräte). */
  relevant: boolean;
}

/** Mitteilungen auf dem Gerät (Plan 17, W3-Push-a): alles an die eigene Person gebunden. */
export const pushApi = {
  config: () => apiRequest<PushConfig>('GET', '/me/push/config'),
  list: () => apiRequest<PushDevice[]>('GET', '/me/push-subscriptions'),
  subscribe: (input: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    apiRequest<PushDevice>('POST', '/me/push-subscriptions', { kind: 'web', ...input }),
  remove: (subscriptionId: string) =>
    apiRequest<undefined>('DELETE', `/me/push-subscriptions/${subscriptionId}`),
};

/** Annahme-Pass der Person (Check-in an der Warenannahme per QR). */
export interface MyIntakePass {
  eventId: string;
  eventTitle: string;
  sellerNumber: number;
  /** Text des QR (`BARSOANNAHME1 <TOKEN>`). */
  qrText: string;
}

/** Ergebnis des Check-ins an der Annahme; ohne Treffer nur `result: 'unknown'`. */
export interface IntakeCheckIn {
  result: 'ok' | 'unknown';
  seller?: IntakeSeller;
  pickupSlot?: { startsAt: string; endsAt: string } | null;
  /** `accepted`: Verkäufer hat selbst zugestimmt; `noted`: Team hat an der Annahme vermerkt, dass er zugestimmt hat. */
  terms?: 'none' | 'accepted' | 'noted' | 'outdated' | 'missing';
  /** Der Basar lässt die Zustimmung zu den Bedingungen bei der Annahme vermerken. */
  confirmTerms?: boolean;
  termsConfirmedAt?: string | null;
}

export const intakePassApi = {
  mine: (registrationId: string) =>
    apiRequest<MyIntakePass>('GET', `/me/registrations/${registrationId}/intake-pass`),
  renew: (registrationId: string) =>
    apiRequest<MyIntakePass>('POST', `/me/registrations/${registrationId}/intake-pass/renew`, {}),
  /** Das Token geht im Body (nie in Pfad oder Query). */
  checkIn: (eventId: string, token: string) =>
    apiRequest<IntakeCheckIn>('POST', `/events/${eventId}/intake/check-in`, { token }),
};

/** Einlass-Pass der Person (W3-Bonus-b). */
export interface MyEntryPass {
  eventId: string;
  eventTitle: string;
  orgName: string;
  startsAt: string;
  /** Beginn des Basars minus früher Einlass. */
  entryFrom: string;
  earlyEntryMinutes: number;
  role: 'helper' | 'seller';
  tierLabel: string | null;
  /** Text des QR (`BARSOEINLASS1 <TOKEN>`). */
  qrText: string;
  /** Der frühe Einlass gilt erst nach Freischaltung (Einstellung des Basars). */
  requiresUnlock: boolean;
  /** Freigeschaltet (Anwesenheit oder Orga) oder keine Freischaltung nötig. */
  unlocked: boolean;
}

/** Antwort der Station „Einlass“. Nie ein Name, der Vorname nur mit dem Recht für Kontaktdaten. */
export interface EntryCheck {
  result: 'ok' | 'too_early' | 'locked' | 'unknown' | 'revoked';
  role: 'helper' | 'seller' | null;
  sellerNumber: number | null;
  tierLabel: string | null;
  entryFrom: string | null;
  firstUsedAt: string | null;
  usedCount: number;
  firstName: string | null;
}

/** Antwort des Helfer-Check-ins per QR (Anwesenheit für die laufende oder nächste Schicht). */
export interface EntryCheckIn {
  result: 'checked_in' | 'already' | 'no_shift' | 'not_now' | 'self' | 'unknown' | 'revoked';
  shiftName: string | null;
  checkedInAt: string | null;
}

/** Eine Person mit frühem Einlass in der Liste der Orga. */
export interface EntryUnlockPerson {
  userId: string;
  sellerNumber: number | null;
  firstName: string;
  lastName: string | null;
  tierLabel: string | null;
  earlyEntryMinutes: number;
  attended: boolean;
  manual: boolean;
  unlocked: boolean;
}

/** Eine Person in der Liste der Station „Einlass“: nur Vorname und Initiale, nie Nachname, E-Mail oder Telefon. */
export interface EntryListPerson {
  userId: string;
  displayName: string;
  sellerNumber: number | null;
  role: 'helper' | 'seller';
  tierLabel: string | null;
  entryFrom: string | null;
  requiresUnlock: boolean;
  unlocked: boolean;
  attended: boolean;
  entered: boolean;
  enteredAt: string | null;
  usedCount: number;
  attendedAt: string | null;
  shiftName: string | null;
  shiftStartsAt: string | null;
  shiftEndsAt: string | null;
  inside: boolean;
}

export const entryApi = {
  people: (eventId: string) =>
    apiRequest<{
      stationOpen: boolean;
      canCheckIn: boolean;
      truncated: boolean;
      people: EntryListPerson[];
    }>('GET', `/events/${eventId}/entry/people`),
  markEntry: (eventId: string, userId: string) =>
    apiRequest<EntryCheck>('POST', `/events/${eventId}/entry/people/${userId}/enter`),
  unmarkEntry: (eventId: string, userId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/entry/people/${userId}/enter`),
  checkInPerson: (eventId: string, userId: string) =>
    apiRequest<EntryCheckIn>('POST', `/events/${eventId}/entry/people/${userId}/check-in`),
  checkIn: (eventId: string, token: string) =>
    apiRequest<EntryCheckIn>('POST', `/events/${eventId}/entry/check-in`, { token }),
  unlocks: (eventId: string) =>
    apiRequest<{ requiresUnlock: boolean; people: EntryUnlockPerson[] }>(
      'GET',
      `/events/${eventId}/entry-unlocks`,
    ),
  setUnlock: (eventId: string, userId: string, unlocked: boolean) =>
    apiRequest<undefined>(
      unlocked ? 'PUT' : 'DELETE',
      `/events/${eventId}/entry-unlocks/${userId}`,
    ),
  mine: () => apiRequest<MyEntryPass[]>('GET', '/me/entry-passes'),
  check: (eventId: string, token: string) =>
    apiRequest<EntryCheck>('POST', `/events/${eventId}/entry/check`, { token }),
};

/** Ein Zeitfenster in der Verwaltung (X8): mit Belegung (nur Zahlen). */
export interface TimeSlot {
  id: string;
  kind: SlotKind;
  startsAt: string;
  endsAt: string;
  capacity: number;
  location: string | null;
  note: string | null;
  booked: number;
}

export interface TimeSlotInput {
  kind: SlotKind;
  startsAt: string;
  endsAt: string;
  capacity: number;
  location?: string | null;
  note?: string | null;
}

export interface TimeSlotGeneratorInput {
  kind: SlotKind;
  from: string;
  to: string;
  lengthMinutes: number;
  capacity: number;
  location?: string | null;
  note?: string | null;
}

/** Buchung in der Verwaltung: Nummer und Name nach dem Etikett-Modus, Kontakt nur mit dem Recht dafür. */
export interface TimeSlotBooking {
  id: string;
  registrationId: string;
  sellerNumber: number | null;
  displayName: string | null;
  overbooked: boolean;
  bookedBy: 'self' | 'organizer';
  createdAt: string;
  contact: { firstName: string; lastName: string; email: string } | null;
}

/** Ein Fenster aus Sicht der Verkäufer: nur freie Plätze als Zahl. */
export interface MyTimeSlot {
  id: string;
  kind: SlotKind;
  startsAt: string;
  endsAt: string;
  location: string | null;
  note: string | null;
  free: number;
  mine: boolean;
}

export interface MyTimeSlots {
  mode: SlotBookingMode;
  canBook: boolean;
  slots: MyTimeSlot[];
}

export interface MyTimeSlotBooking {
  bookingId: string;
  slotId: string;
  kind: SlotKind;
  startsAt: string;
  endsAt: string;
  location: string | null;
  note: string | null;
}

/** Zeitfenster für Abgabe und Abholung (Plan 08, X8). */
export const timeSlotApi = {
  list: (eventId: string) => apiRequest<TimeSlot[]>('GET', `/events/${eventId}/time-slots`),
  create: (eventId: string, input: TimeSlotInput | TimeSlotGeneratorInput) =>
    apiRequest<TimeSlot[]>('POST', `/events/${eventId}/time-slots`, input),
  update: (
    eventId: string,
    slotId: string,
    changes: Partial<Pick<TimeSlotInput, 'startsAt' | 'endsAt' | 'capacity' | 'location' | 'note'>>,
  ) => apiRequest<TimeSlot>('PATCH', `/events/${eventId}/time-slots/${slotId}`, changes),
  remove: (eventId: string, slotId: string) =>
    apiRequest<undefined>('DELETE', `/events/${eventId}/time-slots/${slotId}`),
  bookings: (eventId: string, slotId: string) =>
    apiRequest<TimeSlotBooking[]>('GET', `/events/${eventId}/time-slots/${slotId}/bookings`),
  assign: (eventId: string, slotId: string, registrationId: string, force = false) =>
    apiRequest<TimeSlotBooking>('POST', `/events/${eventId}/time-slots/${slotId}/bookings`, {
      registrationId,
      ...(force ? { force: true } : {}),
    }),
  removeBooking: (eventId: string, slotId: string, bookingId: string) =>
    apiRequest<undefined>(
      'DELETE',
      `/events/${eventId}/time-slots/${slotId}/bookings/${bookingId}`,
    ),
  mine: (registrationId: string) =>
    apiRequest<MyTimeSlots>('GET', `/me/registrations/${registrationId}/time-slots`),
  book: (registrationId: string, kind: SlotKind, slotId: string) =>
    apiRequest<MyTimeSlotBooking>('PUT', `/me/registrations/${registrationId}/time-slots/${kind}`, {
      slotId,
    }),
  cancel: (registrationId: string, kind: SlotKind) =>
    apiRequest<undefined>('DELETE', `/me/registrations/${registrationId}/time-slots/${kind}`),
};
